import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import interact from 'interactjs';
import { finalize, forkJoin, of, switchMap, type Observable } from 'rxjs';
import { NgxExtendedPdfViewerModule, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';

import {
  DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS,
  type BrandSignatureFrameSettings,
  type BrandSummary,
} from '../../models/brand.models';
import type { TemplateCreateDraft, TemplateCreateRoleDraft } from '../../models/template-create.models';
import type {
  TemplateCheckboxSettings,
  TemplateDateSettings,
  TemplateDropdownSettings,
  TemplateField,
  TemplateFieldFontFamily,
  TemplateFieldSettings,
  TemplateFieldTabIndexMode,
  TemplateFieldRect,
  TemplateFieldType,
  TemplateImageSettings,
  TemplatePayload,
  TemplateRadioSettings,
  TemplateRole,
  TemplateSummary,
  TemplateTextboxSettings,
  TemplateUsageSettings,
  TemplateDocumentSettings,
  UploadedPdfFile,
} from '../../models/template.models';
import { TEMPLATE_FIELD_TYPES } from '../../models/template.models';
import { AuthSessionService } from '../../services/auth-session.service';
import { BrandApiService } from '../../services/brand-api.service';
import { GlobalFeedbackService } from '../../services/global-feedback.service';
import { TemplateApiService } from '../../services/template-api.service';
import { ToastService } from '../../services/toast.service';
import {
  FIELD_TIME_FORMAT_OPTIONS,
  USER_DATE_FORMAT_OPTIONS,
  UserDateTimeFormatService,
} from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';
import { FieldConfiguratorWorkspaceComponent } from '../../components/field-configurator/field-configurator-workspace.component';
import type {
  FieldConfiguratorActor,
  FieldConfiguratorOptionSets,
  FieldConfiguratorPreviewMeta,
  FieldConfiguratorSettingAction,
  FieldConfiguratorSettingsPanelOptions,
} from '../../components/field-configurator/field-configurator.models';
import {
  countFieldsForActor,
  fieldSupportsCharacterSpacing,
  filterFieldsForSummary,
  validateFieldConfiguratorStep,
} from '../../components/field-configurator/field-configurator.utils';

interface FieldPaletteItem {
  type: TemplateFieldType;
  label: string;
  iconId: string;
}

interface WalkthroughStep {
  title: string;
  description: string;
  progressLabel: string;
  target: 'left' | 'right';
}

interface PdfTraceState {
  currentStep: 1 | 2;
  viewerMounted: boolean;
  pdfLoading: boolean;
  pdfLoaded: boolean;
  pageCount: number;
  activePage: number;
  hasSource: boolean;
  eventCountThisAttempt: number;
  retryCount: number;
  viewerRenderNonce: number;
}

interface PdfTraceEntry {
  id: number;
  timestampIso: string;
  event: string;
  attemptId: number;
  sourceKind: 'blob' | 'none';
  state: PdfTraceState;
  payloadText: string;
}

interface RuntimeAssetProbeResult {
  label: string;
  url: string;
  ok: boolean;
  status: number | null;
  note?: string;
}

const WALKTHROUGH_KEY_PREFIX = 'document_sign_configure_fields_walkthrough_seen';
const PDF_TRACE_BUFFER_SIZE = 40;
const PDF_TRACE_VISIBLE_ITEMS = 12;
const PDF_STUCK_SNAPSHOT_MS = 8_000;
const PDF_BOOTSTRAP_WATCHDOG_MS = 2_500;
const PDF_BOOTSTRAP_MAX_RETRIES = 1;
const PDF_BOOTSTRAP_FAILURE_MESSAGE =
  'Preview failed to initialize. PDF runtime assets may be missing or unreachable (/assets/viewer-*.mjs, /assets/pdf.worker-*.mjs). Retry preview.';

const FIELD_LABELS: Record<TemplateFieldType, string> = {
  signature: 'Signature',
  initials: 'Initials',
  textbox: 'Textbox',
  date_signed: 'Date signed',
  checkbox: 'Checkbox',
  radio: 'Radio',
  name: 'Name',
  email: 'Email',
  title: 'Title',
  company: 'Company',
  editable_date: 'Editable Date',
  image: 'Image',
  dropdown: 'Dropdown',
};

const FIELD_ICON_IDS: Record<TemplateFieldType, string> = {
  signature: 'signature',
  initials: 'initials',
  textbox: 'textbox',
  date_signed: 'date',
  checkbox: 'checkbox',
  radio: 'radio',
  name: 'person',
  email: 'email',
  title: 'briefcase',
  company: 'briefcase',
  editable_date: 'date',
  image: 'image',
  dropdown: 'dropdown',
};

const FONT_FAMILY_OPTIONS: TemplateFieldFontFamily[] = [
  'Helvetica',
  'Courier',
  'Times New Roman',
  'Noto Sans',
  'Carlito',
];
const FONT_ALIGNMENT_OPTIONS = ['left', 'center', 'right'] as const;
const TEXTBOX_VALIDATION_OPTIONS = ['none', 'email', 'number', 'minimum', 'maximum', 'regex'] as const;
const TEXTBOX_VALIDATION_UI_OPTIONS = ['none', 'email', 'number', 'minimum', 'maximum'] as const;
const OPTION_VALIDATION_OPTIONS = ['none', 'minimum', 'maximum', 'exact'] as const;
const TEXTBOX_RESIZE_OPTIONS = [
  'grow_horizontally',
  'grow_vertically',
  'grow_both',
  'fixed',
  'auto_resize_font',
] as const;
const DATE_FORMAT_OPTIONS = USER_DATE_FORMAT_OPTIONS;
const TIME_FORMAT_OPTIONS = FIELD_TIME_FORMAT_OPTIONS;
const NON_RESIZABLE_FIELD_TYPES = new Set<TemplateFieldType>([
  'date_signed',
  'checkbox',
  'radio',
  'name',
  'email',
  'editable_date',
]);
const LEGACY_AUTO_GROUP_KEY_PATTERN = /^(?:checkbox|radio)-group-[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const DEFAULT_SENDER_ACTIONS = {
  allowEditing: true,
  allowDeletion: true,
  requireDefaultValue: false,
} as const;

const DEFAULT_FONT_SETTINGS = {
  family: 'Helvetica',
  size: 13,
  lineHeight: 15,
  bold: false,
  italic: false,
  underline: false,
  color: '#000000',
  align: 'left',
  rtl: false,
  characterSpacing: 0,
} as const;

const trimOrUndefined = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const sanitizeOptionValues = (values: string[], fallback: string): string[] => {
  const seen = new Set<string>();
  const sanitized: string[] = [];

  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }

    seen.add(trimmed);
    sanitized.push(trimmed);
  }

  if (sanitized.length === 0) {
    return [fallback];
  }

  return sanitized;
};

const clampIntegerOrUndefined = (value: number | undefined): number | undefined => {
  if (value === undefined || Number.isNaN(value)) {
    return undefined;
  }

  return Math.trunc(value);
};

const defaultFieldKeyPrefix = (type: TemplateFieldType): string => {
  switch (type) {
    case 'date_signed':
      return 'DateSigned';
    case 'editable_date':
      return 'EditableDate';
    default:
      return FIELD_LABELS[type].replace(/\s+/g, '');
  }
};

const clampNormalizedSize = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

const textWidthToRect = (
  text: string,
  options: { min: number; max: number; base: number; perChar: number },
): number => {
  const normalized = options.base + text.length * options.perChar;
  return clampNormalizedSize(normalized, options.min, options.max);
};
const COMPACT_FIELD_HEIGHT = 0.04;
const MIN_MEASURED_FIELD_WIDTH = 0.015;
const MAX_MEASURED_FIELD_WIDTH = 0.45;
const MIN_MEASURED_FIELD_HEIGHT = 0.015;
const MAX_MEASURED_FIELD_HEIGHT = 0.16;
const MEASURED_FIELD_SAFETY_PX = 0;

const createDefaultTextboxSettings = (): TemplateTextboxSettings => ({
  readOnly: false,
  maskValue: false,
  defaultText: undefined,
  hintText: undefined,
  resizeType: 'grow_vertically',
  fixedWidth: false,
  characterLimit: undefined,
  validationType: 'none',
  validationValue: undefined,
});

const createDefaultDropdownSettings = (): TemplateDropdownSettings => ({
  hintText: undefined,
  options: ['Option1'],
  defaultOption: undefined,
});

const createDefaultCheckboxSettings = (): TemplateCheckboxSettings => ({
  optionGroupId: undefined,
  value: 'Checkbox1',
  checked: false,
  size: 16,
  values: ['Checkbox1'],
  defaultValues: [],
});

const createDefaultRadioSettings = (): TemplateRadioSettings => ({
  groupName: undefined,
  value: 'Radio1',
  checked: false,
  size: 16,
  values: ['Radio1'],
  defaultValue: undefined,
});

const createDefaultDateSettings = (): TemplateDateSettings => ({
  dateFormat: 'MM/dd/yyyy',
  timeFormat: 'None',
  defaultDate: undefined,
});

const createDefaultImageSettings = (): TemplateImageSettings => ({
  title: undefined,
  instruction: undefined,
  acceptJpgJpeg: true,
  acceptSvg: true,
  acceptPng: true,
  acceptBmp: true,
});

const createDefaultFieldSettings = (): TemplateFieldSettings => ({
  common: {
    senderActions: { ...DEFAULT_SENDER_ACTIONS },
    dataSyncTag: undefined,
    tabIndexMode: 'auto',
    tabIndex: undefined,
    autoPositioningEnabled: false,
  },
  font: { ...DEFAULT_FONT_SETTINGS },
  textbox: createDefaultTextboxSettings(),
  dropdown: createDefaultDropdownSettings(),
  checkbox: createDefaultCheckboxSettings(),
  radio: createDefaultRadioSettings(),
  date: createDefaultDateSettings(),
  image: createDefaultImageSettings(),
});

const normalizeFieldSettings = (settings: TemplateFieldSettings | undefined): TemplateFieldSettings => {
  const defaults = createDefaultFieldSettings();
  const commonRaw = settings?.common;
  const tabIndexMode: TemplateFieldTabIndexMode = commonRaw?.tabIndexMode === 'custom' ? 'custom' : 'auto';
  const tabIndex = tabIndexMode === 'custom' ? clampIntegerOrUndefined(commonRaw?.tabIndex) : undefined;

  const dropdownOptions = sanitizeOptionValues(settings?.dropdown?.options ?? defaults.dropdown?.options ?? [], 'Option1');
  const checkboxValues = sanitizeOptionValues(settings?.checkbox?.values ?? [], 'Checkbox1');
  const radioValues = sanitizeOptionValues(settings?.radio?.values ?? [], 'Radio1');
  const checkboxValue = trimOrUndefined(settings?.checkbox?.value) ?? checkboxValues[0] ?? defaults.checkbox.value;
  const radioValue = trimOrUndefined(settings?.radio?.value) ?? radioValues[0] ?? defaults.radio.value;

  return {
    common: {
      senderActions: {
        allowEditing: commonRaw?.senderActions?.allowEditing ?? defaults.common.senderActions.allowEditing,
        allowDeletion: commonRaw?.senderActions?.allowDeletion ?? defaults.common.senderActions.allowDeletion,
        requireDefaultValue:
          commonRaw?.senderActions?.requireDefaultValue ?? defaults.common.senderActions.requireDefaultValue,
      },
      dataSyncTag: trimOrUndefined(commonRaw?.dataSyncTag),
      tabIndexMode,
      tabIndex,
      autoPositioningEnabled: commonRaw?.autoPositioningEnabled ?? defaults.common.autoPositioningEnabled,
    },
    font: {
      family:
        settings?.font?.family && FONT_FAMILY_OPTIONS.includes(settings.font.family)
          ? settings.font.family
          : defaults.font.family,
      size: clampIntegerOrUndefined(settings?.font?.size) ?? defaults.font.size,
      lineHeight: clampIntegerOrUndefined(settings?.font?.lineHeight) ?? defaults.font.lineHeight,
      bold: settings?.font?.bold ?? defaults.font.bold,
      italic: settings?.font?.italic ?? defaults.font.italic,
      underline: settings?.font?.underline ?? defaults.font.underline,
      color: trimOrUndefined(settings?.font?.color) ?? defaults.font.color,
      align:
        settings?.font?.align && FONT_ALIGNMENT_OPTIONS.includes(settings.font.align)
          ? settings.font.align
          : defaults.font.align,
      rtl: settings?.font?.rtl ?? defaults.font.rtl,
      characterSpacing: clampIntegerOrUndefined(settings?.font?.characterSpacing) ?? defaults.font.characterSpacing,
    },
    textbox: {
      ...defaults.textbox,
      ...settings?.textbox,
      resizeType:
        settings?.textbox?.resizeType && TEXTBOX_RESIZE_OPTIONS.includes(settings.textbox.resizeType)
          ? settings.textbox.resizeType
          : defaults.textbox.resizeType,
      validationType:
        settings?.textbox?.validationType && TEXTBOX_VALIDATION_OPTIONS.includes(settings.textbox.validationType)
          ? settings.textbox.validationType
          : defaults.textbox.validationType,
      defaultText: trimOrUndefined(settings?.textbox?.defaultText),
      hintText: trimOrUndefined(settings?.textbox?.hintText),
      characterLimit: clampIntegerOrUndefined(settings?.textbox?.characterLimit),
      validationValue: trimOrUndefined(settings?.textbox?.validationValue),
    },
    dropdown: {
      ...defaults.dropdown,
      ...settings?.dropdown,
      hintText: trimOrUndefined(settings?.dropdown?.hintText),
      options: dropdownOptions,
      defaultOption:
        trimOrUndefined(settings?.dropdown?.defaultOption) &&
        dropdownOptions.includes(trimOrUndefined(settings?.dropdown?.defaultOption)!)
          ? trimOrUndefined(settings?.dropdown?.defaultOption)
          : undefined,
    },
    checkbox: {
      ...defaults.checkbox,
      ...settings?.checkbox,
      optionGroupId: trimOrUndefined(settings?.checkbox?.optionGroupId),
      value: checkboxValue,
      values: checkboxValues,
      defaultValues: sanitizeOptionValues(settings?.checkbox?.defaultValues ?? [], ''),
      checked:
        settings?.checkbox?.checked ??
        sanitizeOptionValues(settings?.checkbox?.defaultValues ?? [], '').includes(checkboxValue),
      size: clampIntegerOrUndefined(settings?.checkbox?.size) ?? defaults.checkbox.size,
    },
    radio: {
      ...defaults.radio,
      ...settings?.radio,
      groupName: trimOrUndefined(settings?.radio?.groupName),
      value: radioValue,
      values: radioValues,
      defaultValue: trimOrUndefined(settings?.radio?.defaultValue),
      checked: settings?.radio?.checked ?? trimOrUndefined(settings?.radio?.defaultValue) === radioValue,
      size: clampIntegerOrUndefined(settings?.radio?.size) ?? defaults.radio.size,
    },
    date: {
      ...defaults.date,
      ...settings?.date,
      defaultDate: trimOrUndefined(settings?.date?.defaultDate),
    },
    image: {
      ...defaults.image,
      ...settings?.image,
      title: trimOrUndefined(settings?.image?.title),
      instruction: trimOrUndefined(settings?.image?.instruction),
    },
  };
};

const normalizeFieldSettingsForType = (
  fieldType: TemplateFieldType,
  settings: TemplateFieldSettings | undefined,
): TemplateFieldSettings => {
  const normalized = normalizeFieldSettings(settings);
  if (fieldSupportsCharacterSpacing(fieldType)) {
    return normalized;
  }

  return {
    ...normalized,
    font: {
      ...normalized.font,
      characterSpacing: 0,
    },
  };
};

const FIELD_PALETTE: FieldPaletteItem[] = TEMPLATE_FIELD_TYPES.map((type) => ({
  type,
  label: FIELD_LABELS[type],
  iconId: FIELD_ICON_IDS[type],
}));

const WALKTHROUGH_STEPS: WalkthroughStep[] = [
  {
    title: 'Drag & drop fields',
    description: 'Drag a field tile from the left panel and drop it on the document where the signer should fill.',
    progressLabel: '1 of 3',
    target: 'left',
  },
  {
    title: 'Assigning fields to each signer',
    description: 'Select each signer from the list and assign at least one field before saving the template.',
    progressLabel: '2 of 3',
    target: 'left',
  },
  {
    title: 'Ready to save and use',
    description: 'Click Save template to create the template. You can use it later for sending signature requests.',
    progressLabel: '3 of 3',
    target: 'right',
  },
];

const ROLE_COLOR_PALETTE = ['#7E21D9', '#2563EB', '#059669', '#D97706', '#DB2777', '#0891B2'];
const ROLE_COLOR_GOLDEN_ANGLE_DEGREES = 137.508;
const ROLE_COLOR_HUE_OFFSET_DEGREES = 24;

const createDefaultUsageSettings = (): TemplateUsageSettings => ({
  allowAddMoreFiles: true,
  allowReplaceRearrangeDeleteFiles: true,
  allowAddMoreRecipients: true,
  allowEditDocumentMessage: true,
  allowAddNewFields: true,
  allowEditOrDeleteFields: true,
});

const createDefaultDocumentSettings = (): TemplateDocumentSettings => ({
  brandId: undefined,
  brandName: undefined,
  allowPrintAndSign: false,
});

const createEmptyRole = (): TemplateCreateRoleDraft => ({
  id: `role-${crypto.randomUUID()}`,
  role: '',
  defaultRecipientName: '',
  defaultRecipientEmail: '',
});

const createInitialDraft = (): TemplateCreateDraft => ({
  templateTitle: '',
  templateDescription: '',
  selectedFile: null,
  cc: '',
  documentTitle: '',
  documentMessage: '',
  tags: '',
  signingOrderEnabled: false,
  roles: [createEmptyRole()],
  fields: [],
  usageSettings: createDefaultUsageSettings(),
  documentSettings: createDefaultDocumentSettings(),
});

@Component({
  selector: 'app-template-create',
  imports: [CommonModule, FormsModule, DragDropModule, NgxExtendedPdfViewerModule, FieldConfiguratorWorkspaceComponent],
  templateUrl: './template-create.component.html',
  styleUrl: './template-create.component.scss',
})
export class TemplateCreateComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') private readonly fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('viewerHost', { read: ElementRef }) private readonly viewerHost?: ElementRef<HTMLElement>;

  readonly fieldPalette = FIELD_PALETTE;
  readonly walkthroughSteps = WALKTHROUGH_STEPS;
  readonly fontFamilyOptions = FONT_FAMILY_OPTIONS;
  readonly fontAlignmentOptions = FONT_ALIGNMENT_OPTIONS;
  readonly textboxValidationOptions = TEXTBOX_VALIDATION_UI_OPTIONS;
  readonly optionValidationOptions = OPTION_VALIDATION_OPTIONS;
  readonly textboxResizeOptions = TEXTBOX_RESIZE_OPTIONS;
  readonly dateFormatOptions = DATE_FORMAT_OPTIONS;
  readonly timeFormatOptions = TIME_FORMAT_OPTIONS;

  currentStep: 1 | 2 = 1;
  stepTwoMode: 'configure' | 'preview' = 'configure';
  draft: TemplateCreateDraft = createInitialDraft();
  activeRoleId = '';
  previewRecipientFilter: 'all' | string = 'all';
  activePage = 1;
  pageCount = 1;
  selectedFieldId: string | null = null;
  private interactingFieldId: string | null = null;
  private interactingOverlayPage: number | null = null;
  stepOneSubmitted = false;
  stepTwoSubmitted = false;
  fileError = '';
  stepTwoError = '';
  savingTemplate = false;
  loadingTemplateForEdit = false;
  editLoadError = '';
  pdfLoading = false;
  pdfLoaded = false;
  pdfLoadError = '';
  pdfLoadErrorDetail = '';
  showSaveSuccessModal = false;
  walkthroughVisible = false;
  walkthroughIndex = 0;
  lastSavedTemplateName = '';
  dragFieldType: TemplateFieldType | null = null;
  pdfObjectUrl: string | null = null;
  viewerMounted = false;
  pdfLoadAttemptId = 0;
  readonly enablePdfEventTrace = false;
  readonly pdfTraceVisibleItems = PDF_TRACE_VISIBLE_ITEMS;
  pdfTraceEntries: PdfTraceEntry[] = [];
  pdfEventCountThisAttempt = 0;
  pdfBootstrapRetryCount = 0;
  viewerRenderNonce = 0;
  showViewerInstance = true;
  pdfBootstrapError = '';
  templateIdForEdit: string | null = null;
  publicTemplateConfigToken: string | null = null;
  publicTemplateId: string | null = null;
  publicTemplateConfigInitializing = false;
  publicTemplateSaveMessage = '';
  brands: BrandSummary[] = [];
  brandsLoading = false;

  private overlaySyncTimer: ReturnType<typeof setTimeout> | null = null;
  private fieldRectsNormalizedForSession = false;
  private pdfStuckSnapshotTimer: ReturnType<typeof setTimeout> | null = null;
  private pdfBootstrapWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
  private pdfTraceEntryIdCounter = 0;
  private roleColorGeneratedIndex = 0;
  private readonly roleColorRegistry = new Map<string, string>();
  private readonly releasedGeneratedRoleColors: string[] = [];

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly api = inject(TemplateApiService);
  private readonly brandApi = inject(BrandApiService);
  private readonly feedback = inject(GlobalFeedbackService);
  private readonly authSession = inject(AuthSessionService);
  private readonly userDateTimeFormat = inject(UserDateTimeFormatService);
  private readonly toast = inject(ToastService);
  private currentSourceFile: UploadedPdfFile | null = null;
  private sourceFileReplaced = false;

  get isEditMode(): boolean {
    return this.templateIdForEdit !== null;
  }

  get isPublicTemplateConfigureMode(): boolean {
    return !!this.publicTemplateConfigToken;
  }

  get submitButtonText(): string {
    if (this.savingTemplate) {
      if (this.isPublicTemplateConfigureMode) {
        return 'Saving...';
      }

      return this.isEditMode ? 'Updating...' : 'Saving...';
    }

    if (this.isPublicTemplateConfigureMode) {
      return 'Save fields';
    }

    return this.isEditMode ? 'Update template' : 'Save template';
  }

  get saveSuccessText(): string {
    if (this.isEditMode) {
      return `Template "${this.lastSavedTemplateName}" has been updated successfully. Do you want to use this template now?`;
    }

    return `Template "${this.lastSavedTemplateName}" has been saved successfully. Do you want to use this template now?`;
  }

  get hasSelectedFile(): boolean {
    return this.draft.selectedFile !== null;
  }

  get visibleFileName(): string {
    return this.draft.selectedFile?.name ?? '';
  }

  get hasTemplateTitleError(): boolean {
    return this.stepOneSubmitted && this.draft.templateTitle.trim().length === 0;
  }

  get hasFileError(): boolean {
    return this.stepOneSubmitted && this.draft.selectedFile === null;
  }

  get hasDocumentTitleError(): boolean {
    return this.stepOneSubmitted && this.draft.documentTitle.trim().length === 0;
  }

  get activeRoleName(): string {
    return this.roleNameById(this.activeRoleId) ?? 'Unassigned';
  }

  get selectedField(): TemplateField | null {
    if (!this.selectedFieldId) {
      return null;
    }

    return this.draft.fields.find((field) => field.id === this.selectedFieldId) ?? null;
  }

  get selectedFieldSettingsTitle(): string {
    const field = this.selectedField;
    if (!field) {
      return 'Field settings';
    }

    return `${FIELD_LABELS[field.type]} settings`;
  }

  get fieldConfiguratorActors(): FieldConfiguratorActor[] {
    return this.draft.roles.map((role) => ({
      id: role.id,
      label: role.role,
      kind: 'role',
      color: this.roleChipColor(role.id),
      meta: {
        name: role.defaultRecipientName || undefined,
        email: role.defaultRecipientEmail || undefined,
      },
    }));
  }

  get fieldConfiguratorPreviewMeta(): FieldConfiguratorPreviewMeta {
    return {
      fileLabel: this.visibleFileName,
      title: this.draft.documentTitle,
      message: this.draft.documentMessage,
      canEditTitle: !this.isPublicTemplateConfigureMode,
      canEditMessage: !this.isPublicTemplateConfigureMode,
    };
  }

  readonly fieldConfiguratorSettingsPanelOptions: FieldConfiguratorSettingsPanelOptions = {
    actorLabel: 'Assigned to',
    pageMetaLabel: 'Index position',
    fieldsetDisabled: false,
    showDefaultDropdownOption: true,
    showImageAcceptOptions: true,
    showAddDefaultImageButton: true,
    showAutoPositionConfigureButton: true,
    showConditionalLogicSection: true,
  };

  readonly fieldConfiguratorOptionSets: FieldConfiguratorOptionSets = {
    fontFamilyOptions: this.fontFamilyOptions,
    fontAlignmentOptions: this.fontAlignmentOptions,
    textboxResizeOptions: this.textboxResizeOptions,
    textboxValidationOptions: this.textboxValidationOptions,
    dateFormatOptions: this.dateFormatOptions,
    timeFormatOptions: this.timeFormatOptions,
  };

  get canPlaceFields(): boolean {
    return this.stepTwoMode === 'configure' && this.pdfLoaded && !this.pdfLoading && this.pdfLoadError.length === 0;
  }

  get viewerPdfSource(): string | null {
    return this.pdfObjectUrl;
  }

  get viewerPageBinding(): number | undefined {
    if (!this.pdfLoaded) {
      return undefined;
    }

    return this.activePage;
  }

  get currentWalkthroughStep(): WalkthroughStep {
    return this.walkthroughSteps[this.walkthroughIndex] ?? this.walkthroughSteps[0];
  }

  get pdfTracePanelEntries(): PdfTraceEntry[] {
    return this.pdfTraceEntries.slice(-this.pdfTraceVisibleItems).reverse();
  }

  ngOnInit(): void {
    const publicConfigToken = this.route.snapshot.routeConfig?.path === 'developers/api/configure-template-fields/:token'
      ? this.route.snapshot.paramMap.get('token')
      : null;
    if (publicConfigToken) {
      this.publicTemplateConfigToken = publicConfigToken;
      this.loadBrands();
      this.currentStep = 2;
      this.stepTwoMode = 'configure';
      this.publicTemplateConfigInitializing = true;
      this.loadPublicTemplateConfigSession(publicConfigToken);
      return;
    }

    this.loadBrands();

    const templateId = this.route.snapshot.paramMap.get('id');
    if (!templateId) {
      return;
    }

    this.templateIdForEdit = templateId;
    this.loadTemplateForEdit(templateId);
  }

  ngOnDestroy(): void {
    if (this.overlaySyncTimer) {
      clearTimeout(this.overlaySyncTimer);
      this.overlaySyncTimer = null;
    }

    this.clearFieldInteractionState();
    this.clearPdfStuckSnapshotTimer();
    this.clearPdfBootstrapWatchdogTimer();

    this.viewerHost?.nativeElement
      .querySelectorAll<HTMLElement>('.template-field')
      .forEach((element) => interact(element).unset());

    this.clearPdfSources();
  }

  @HostListener('document:keydown', ['$event'])
  handleDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Delete' || event.defaultPrevented) {
      return;
    }

    if (this.currentStep !== 2 || this.stepTwoMode !== 'configure' || !this.selectedFieldId) {
      return;
    }

    if (this.shouldIgnoreFieldDeleteShortcut(event.target)) {
      return;
    }

    event.preventDefault();
    this.removeSelectedField();
  }

  goBackToTemplates(): void {
    if (this.isPublicTemplateConfigureMode) {
      return;
    }

    this.clearWorkingPdfState();
    void this.router.navigate(['/templates']);
  }

  goToStepOne(): void {
    if (this.isPublicTemplateConfigureMode) {
      return;
    }

    this.clearFieldInteractionState();
    this.currentStep = 1;
    this.stepTwoMode = 'configure';
    this.walkthroughVisible = false;
    this.dragFieldType = null;
    this.viewerMounted = false;
    this.pdfLoadError = '';
    this.pdfLoadErrorDetail = '';
    this.pdfLoading = false;
    this.pdfLoaded = false;
    this.pdfBootstrapError = '';
    this.clearPdfStuckSnapshotTimer();
    this.clearPdfBootstrapWatchdogTimer();
    this.tracePdfEvent('goToStepOne');
    this.cdr.markForCheck();
  }

  goToStepTwo(): void {
    if (this.isPublicTemplateConfigureMode) {
      return;
    }

    if (this.loadingTemplateForEdit) {
      return;
    }

    this.stepOneSubmitted = true;

    if (this.isStepOneInvalid()) {
      this.cdr.markForCheck();
      return;
    }

    if (!this.ensurePdfSources()) {
      this.fileError = 'File preview could not be prepared. Please reselect the PDF.';
      this.cdr.markForCheck();
      return;
    }

    this.clearFieldInteractionState();
    this.currentStep = 2;
    this.stepTwoMode = 'configure';
    this.stepTwoError = '';
    this.pdfLoading = true;
    this.pdfLoaded = false;
    this.pdfLoadError = '';
    this.pdfLoadErrorDetail = '';
    this.pdfBootstrapError = '';
    this.viewerMounted = this.viewerPdfSource !== null;
    this.showViewerInstance = true;
    this.dragFieldType = null;
    this.selectedFieldId = null;
    this.pageCount = 1;
    this.activePage = 1;
    this.activeRoleId = this.draft.roles[0]?.id ?? '';
    this.previewRecipientFilter = 'all';
    this.fieldRectsNormalizedForSession = false;

    this.maybeShowWalkthrough();
    this.beginPdfLoadAttempt('goToStepTwo');
    this.tracePdfEvent('viewer-bootstrap-rendered', { sourcePresent: this.viewerPdfSource !== null });
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  triggerChooseFile(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);

    if (!file) {
      return;
    }

    if (!this.isPdfFile(file)) {
      this.fileError = 'Only PDF files are allowed.';
      this.draft.selectedFile = null;
      this.clearWorkingPdfState();
      input.value = '';
      this.cdr.markForCheck();
      return;
    }

    this.fileError = '';
    this.draft.selectedFile = file;
    if (this.isEditMode) {
      this.sourceFileReplaced = true;
    }
    this.clearWorkingPdfState();

    if (!this.ensurePdfSources()) {
      this.fileError = 'File preview could not be prepared. Please reselect the PDF.';
    }

    this.cdr.markForCheck();
  }

  removeSelectedFile(): void {
    this.draft.selectedFile = null;
    this.fileError = '';
    if (this.isEditMode) {
      this.sourceFileReplaced = true;
    }
    this.draft.fields = [];
    this.selectedFieldId = null;
    this.clearWorkingPdfState();

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }

    this.cdr.markForCheck();
  }

  addRole(): void {
    this.draft.roles = [...this.draft.roles, createEmptyRole()];
    this.syncRoleColorRegistry();
    this.cdr.markForCheck();
  }

  removeRole(roleId: string): void {
    if (this.draft.roles.length === 1) {
      this.draft.roles = [createEmptyRole()];
      this.activeRoleId = this.draft.roles[0].id;
      this.syncRoleColorRegistry();
      this.cdr.markForCheck();
      return;
    }

    const updatedRoles = this.draft.roles.filter((role) => role.id !== roleId);
    this.draft.roles = updatedRoles;

    if (!updatedRoles.some((role) => role.id === this.activeRoleId)) {
      this.activeRoleId = updatedRoles[0]?.id ?? '';
    }

    this.draft.fields = this.draft.fields.filter((field) => field.role !== roleId);
    if (this.selectedFieldId && !this.draft.fields.some((field) => field.id === this.selectedFieldId)) {
      this.selectedFieldId = null;
    }
    this.syncRoleColorRegistry();
    this.cdr.markForCheck();
  }

  dropRole(event: CdkDragDrop<TemplateCreateRoleDraft[]>): void {
    if (!this.draft.signingOrderEnabled || event.previousIndex === event.currentIndex) {
      return;
    }

    const roles = [...this.draft.roles];
    moveItemInArray(roles, event.previousIndex, event.currentIndex);
    this.draft.roles = roles;
    this.syncRoleColorRegistry();
    this.cdr.markForCheck();
  }

  roleError(role: TemplateCreateRoleDraft): string {
    if (!this.stepOneSubmitted) {
      return '';
    }

    if (!role.role.trim()) {
      return 'Role is required';
    }

    return '';
  }

  setStepTwoMode(mode: 'configure' | 'preview'): void {
    this.clearFieldInteractionState();
    this.stepTwoMode = mode;
    this.walkthroughVisible = false;
    this.dragFieldType = null;

    if (mode === 'preview') {
      this.previewRecipientFilter = 'all';
    }

    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  onPagesLoaded(event: unknown): void {
    const pagesCount = this.extractPagesCount(event);
    this.notePdfViewerEvent('pagesLoaded', { pagesCount, rawEvent: event });

    if (pagesCount > 0) {
      this.pageCount = pagesCount;
    }

    this.activePage = this.clamp(Math.trunc(this.activePage), 1, this.pageCount);
    if (this.pdfLoading && pagesCount > 0) {
      this.finalizePdfLoaded('pagesLoaded-fallback', { pagesCount });
      return;
    }

    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  onPageRendered(event: unknown): void {
    this.notePdfViewerEvent('pageRendered', event);
    this.finalizePdfLoaded('pageRendered', event);
    this.scheduleOverlaySync();
  }

  onPageChanged(event: unknown): void {
    this.notePdfViewerEvent('pageChangedRaw', event);
    const page = this.extractPageChange(event);
    if (page <= 0) {
      return;
    }

    this.activePage = this.clamp(Math.trunc(page), 1, this.pageCount);
    this.scheduleOverlaySync();
  }

  onThumbnailSelected(page: number): void {
    this.activePage = this.clamp(page, 1, this.pageCount);
    this.scheduleOverlaySync();
  }

  onPreviewRecipientFilterChanged(): void {
    this.scheduleOverlaySync();
  }

  handleFieldConfiguratorPreviewMetaChange(change: Partial<FieldConfiguratorPreviewMeta>): void {
    if (typeof change.title === 'string') {
      this.draft.documentTitle = change.title;
    }
    if (typeof change.message === 'string') {
      this.draft.documentMessage = change.message;
    }
    this.cdr.markForCheck();
  }

  handleFieldConfiguratorSettingsAction(action: FieldConfiguratorSettingAction): void {
    switch (action.type) {
      case 'update-role':
        this.updateSelectedFieldRole(action.payload);
        break;
      case 'update-required':
        this.updateSelectedFieldRequired(action.payload);
        break;
      case 'update-sender-action':
        this.updateSelectedFieldSenderAction(action.payload.key, action.payload.value);
        break;
      case 'update-key':
        this.updateSelectedFieldKey(action.payload);
        break;
      case 'update-textbox':
        this.updateSelectedFieldTextboxPatch(action.payload);
        break;
      case 'update-dropdown':
        this.updateSelectedFieldDropdownPatch(action.payload);
        break;
      case 'move-dropdown-option':
        this.moveSelectedDropdownOption(action.payload.index, action.payload.direction);
        break;
      case 'update-dropdown-option':
        this.updateSelectedDropdownOption(action.payload.index, action.payload.value);
        break;
      case 'remove-dropdown-option':
        this.removeSelectedDropdownOption(action.payload);
        break;
      case 'add-dropdown-option':
        this.addSelectedDropdownOption();
        break;
      case 'update-checkbox':
        this.updateSelectedFieldCheckboxPatch(action.payload);
        break;
      case 'toggle-checkbox-default':
        this.toggleSelectedCheckboxDefault(action.payload.option, action.payload.checked);
        break;
      case 'update-checkbox-value':
        this.updateSelectedCheckboxValue(action.payload.index, action.payload.value);
        break;
      case 'remove-checkbox-value':
        this.removeSelectedCheckboxValue(action.payload);
        break;
      case 'add-checkbox-value':
        this.addSelectedCheckboxValue();
        break;
      case 'update-radio':
        this.updateSelectedFieldRadioPatch(action.payload);
        break;
      case 'toggle-radio-default':
        this.toggleSelectedRadioDefault(action.payload.option, action.payload.checked);
        break;
      case 'update-radio-value':
        this.updateSelectedRadioValue(action.payload.index, action.payload.value);
        break;
      case 'remove-radio-value':
        this.removeSelectedRadioValue(action.payload);
        break;
      case 'add-radio-value':
        this.addSelectedRadioValue();
        break;
      case 'update-date':
        this.updateSelectedFieldDatePatch(action.payload);
        break;
      case 'update-image':
        this.updateSelectedFieldImagePatch(action.payload);
        break;
      case 'update-data-sync-tag':
        this.updateSelectedFieldDataSyncTag(action.payload);
        break;
      case 'update-position':
        this.updateSelectedFieldPosition(action.payload.axis, action.payload.value);
        break;
      case 'update-tab-index-mode':
        this.updateSelectedFieldTabIndexMode(action.payload);
        break;
      case 'update-tab-index':
        this.updateSelectedFieldTabIndex(action.payload);
        break;
      case 'update-font':
        this.updateSelectedFieldFontPatch(action.payload);
        break;
      case 'update-auto-positioning-enabled':
        this.updateSelectedFieldAutoPositioningEnabled(action.payload);
        break;
    }
  }

  onPdfLoadingStarts(): void {
    this.pdfLoading = true;
    this.pdfLoaded = false;
    this.pdfLoadError = '';
    this.pdfLoadErrorDetail = '';
    this.notePdfViewerEvent('pdfLoadingStarts');
    this.schedulePdfStuckSnapshot();
    this.cdr.markForCheck();
  }

  onPdfLoaded(): void {
    this.notePdfViewerEvent('pdfLoaded');
    this.finalizePdfLoaded('pdfLoaded');
  }

  onPdfLoadingFailed(error: unknown): void {
    const detail = this.extractPdfErrorDetail(error);
    this.clearPdfStuckSnapshotTimer();
    this.clearPdfBootstrapWatchdogTimer();
    this.pdfLoading = false;
    this.pdfLoaded = false;
    this.dragFieldType = null;
    this.pdfLoadErrorDetail = '';
    this.pdfLoadError = '';
    this.notePdfViewerEvent('pdfLoadingFailed', { detail, rawError: error });
    this.clearDropHoverClasses();
    this.toast.error('Invalid PDF file. Please choose a valid PDF document.');
    this.fileError = 'Invalid PDF file. Please choose a valid PDF document.';
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  goToStepOneForPdfReselect(): void {
    this.currentStep = 1;
    this.stepTwoMode = 'configure';
    this.pdfLoading = false;
    this.pdfLoaded = false;
    this.pdfLoadError = '';
    this.pdfLoadErrorDetail = '';
    this.pdfBootstrapError = '';
    this.viewerMounted = false;
    this.dragFieldType = null;
    this.walkthroughVisible = false;
    this.fileError = 'Please reselect a PDF file.';
    this.clearPdfStuckSnapshotTimer();
    this.clearPdfBootstrapWatchdogTimer();
    this.tracePdfEvent('goToStepOneForPdfReselect');
    this.cdr.markForCheck();
  }

  retryPdfPreview(): void {
    const selectedFile = this.draft.selectedFile;
    if (!selectedFile) {
      this.fileError = 'A PDF file is required.';
      this.currentStep = 1;
      this.cdr.markForCheck();
      return;
    }

    this.pdfBootstrapError = '';
    this.pdfLoadError = '';
    this.pdfLoadErrorDetail = '';
    this.pdfLoaded = false;
    this.pdfLoading = true;
    this.clearDropHoverClasses();
    this.beginPdfLoadAttempt('manual-retry-preview');
    this.performBootstrapRetry('manual-retry-preview');
    this.cdr.markForCheck();
  }

  onPaletteDragStart(event: DragEvent, type: TemplateFieldType): void {
    if (!this.canPlaceFields) {
      event.preventDefault();
      return;
    }

    this.dragFieldType = type;
    this.stepTwoError = '';

    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', type);
      event.dataTransfer.effectAllowed = 'copy';
    }

    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  onPaletteDragEnd(): void {
    this.dragFieldType = null;
    this.clearDropHoverClasses();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  onPaletteTileClick(type: TemplateFieldType): void {
    if (!this.canPlaceFields) {
      return;
    }

    this.addField(type);
  }

  addField(type: TemplateFieldType): void {
    if (!this.canPlaceFields) {
      return;
    }

    const activeRole = this.activeRoleId || this.draft.roles[0]?.id || '';
    if (!activeRole) {
      this.setStepTwoError('Add a role before configuring fields.');
      return;
    }

    const pageNumber = this.clamp(this.activePage, 1, Math.max(this.pageCount, 1));
    const pageElement = this.resolvePageElement(pageNumber);
    const measuredRect = this.computeFitRectForNewField(type, activeRole, pageNumber, pageElement);
    if (!measuredRect) {
      this.setStepTwoError('Please wait for page rendering to finish, then add the field again.');
      this.scheduleOverlaySync();
      this.cdr.markForCheck();
      return;
    }

    const seedRect = this.clampRectToPage(measuredRect);
    if (this.wouldOverlapField(null, pageNumber, seedRect)) {
      this.setStepTwoError("Can't place field on top of another field.");
      return;
    }

    const createdField = this.createField(type, activeRole, pageNumber, seedRect);
    this.draft.fields = [...this.draft.fields, createdField];
    this.selectedFieldId = createdField.id;
    this.stepTwoError = '';
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  removeField(fieldId: string): void {
    if (this.interactingFieldId === fieldId) {
      this.clearFieldInteractionState();
    }

    this.draft.fields = this.draft.fields.filter((field) => field.id !== fieldId);

    if (this.selectedFieldId === fieldId) {
      this.selectedFieldId = null;
    }

    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  removeSelectedField(): void {
    if (!this.selectedFieldId) {
      return;
    }

    this.removeField(this.selectedFieldId);
  }

  selectField(fieldId: string): void {
    if (!this.draft.fields.some((field) => field.id === fieldId)) {
      return;
    }

    this.selectedFieldId = fieldId;
    this.ensureSelectedChoiceGroupConsistency();
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  clearSelectedField(): void {
    this.selectedFieldId = null;
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  updateSelectedFieldRole(roleId: string): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }
    const groupIds = this.choiceGroupFieldIds(selected);
    if (groupIds.length <= 1) {
      this.updateSelectedField({ role: roleId });
      return;
    }
    this.draft.fields = this.draft.fields.map((field) => (groupIds.includes(field.id) ? { ...field, role: roleId } : field));
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  updateSelectedFieldRequired(required: boolean): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }
    const groupIds = this.choiceGroupFieldIds(selected);
    if (groupIds.length <= 1) {
      this.updateSelectedField({ required });
      return;
    }
    this.draft.fields = this.draft.fields.map((field) => (groupIds.includes(field.id) ? { ...field, required } : field));
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  updateSelectedFieldKey(fieldKey: string): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const sanitized = trimOrUndefined(fieldKey) ?? selected.fieldKey;
    this.updateSelectedField({ fieldKey: sanitized });
  }

  updateSelectedFieldDataSyncTag(dataSyncTag: string): void {
    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      common: {
        ...settings.common,
        dataSyncTag: trimOrUndefined(dataSyncTag),
      },
    }));
  }

  updateSelectedFieldTabIndexMode(mode: TemplateFieldTabIndexMode): void {
    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      common: {
        ...settings.common,
        tabIndexMode: mode,
        tabIndex: mode === 'custom' ? settings.common.tabIndex ?? 1 : undefined,
      },
    }));
  }

  updateSelectedFieldTabIndex(value: number): void {
    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      common: {
        ...settings.common,
        tabIndex: Math.max(1, Math.trunc(value || 1)),
      },
    }));
  }

  updateSelectedFieldSenderAction(
    key: 'allowEditing' | 'allowDeletion' | 'requireDefaultValue',
    value: boolean,
  ): void {
    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      common: {
        ...settings.common,
        senderActions: {
          ...settings.common.senderActions,
          [key]: value,
        },
      },
    }));
  }

  updateSelectedFieldAutoPositioningEnabled(value: boolean): void {
    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      common: {
        ...settings.common,
        autoPositioningEnabled: value,
      },
    }));
  }

  updateSelectedFieldFontPatch(
    patch: Partial<TemplateFieldSettings['font']>,
  ): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      font: {
        ...settings.font,
        ...patch,
        ...(fieldSupportsCharacterSpacing(selected.type) ? {} : { characterSpacing: 0 }),
      },
    }));
  }

  updateSelectedFieldTextboxPatch(patch: Partial<TemplateTextboxSettings>): void {
    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      textbox: {
        ...settings.textbox,
        ...patch,
      } as TemplateTextboxSettings,
    }));
  }

  updateSelectedFieldDropdownPatch(patch: Partial<TemplateDropdownSettings>): void {
    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      dropdown: {
        ...settings.dropdown,
        ...patch,
      } as TemplateDropdownSettings,
    }));
  }

  updateSelectedFieldCheckboxPatch(patch: Partial<TemplateCheckboxSettings>): void {
    if (patch.optionGroupId !== undefined) {
      const selected = this.selectedField;
      if (!selected || selected.type !== 'checkbox') {
        return;
      }
      const values = this.checkboxGroupValuesModel();
      const sourceGroupId = this.resolveCheckboxGroupId(selected);
      const targetGroupId = trimOrUndefined(patch.optionGroupId) ?? sourceGroupId;
      this.syncSelectedCheckboxOptionFields(values, this.checkboxGroupDefaultValuesModel(), {
        sourceGroupId,
        targetGroupId,
      });
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      checkbox: {
        ...settings.checkbox,
        ...patch,
      } as TemplateCheckboxSettings,
    }));
    if (patch.size !== undefined) {
      const selected = this.selectedField;
      if (selected?.type === 'checkbox') {
        this.syncSelectedCheckboxOptionFields(this.checkboxGroupValuesModel(), this.checkboxGroupDefaultValuesModel());
      }
    }
  }

  updateSelectedFieldRadioPatch(patch: Partial<TemplateRadioSettings>): void {
    if (patch.groupName !== undefined) {
      const selected = this.selectedField;
      if (!selected || selected.type !== 'radio') {
        return;
      }
      const values = this.radioGroupValuesModel();
      const sourceGroupName = this.resolveRadioGroupName(selected);
      const targetGroupName = trimOrUndefined(patch.groupName) ?? sourceGroupName;
      this.syncSelectedRadioOptionFields(values, this.radioGroupDefaultValueModel(), {
        sourceGroupName,
        targetGroupName,
      });
      return;
    }

    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      radio: {
        ...settings.radio,
        ...patch,
      } as TemplateRadioSettings,
    }));
    if (patch.size !== undefined) {
      const selected = this.selectedField;
      if (selected?.type === 'radio') {
        this.syncSelectedRadioOptionFields(this.radioGroupValuesModel(), this.radioGroupDefaultValueModel());
      }
    }
  }

  updateSelectedFieldDatePatch(patch: Partial<TemplateDateSettings>): void {
    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      date: {
        ...settings.date,
        ...patch,
      } as TemplateDateSettings,
    }));
  }

  updateSelectedFieldImagePatch(patch: Partial<TemplateImageSettings>): void {
    this.updateSelectedFieldSettings((settings) => ({
      ...settings,
      image: {
        ...settings.image,
        ...patch,
      } as TemplateImageSettings,
    }));
  }

  addSelectedDropdownOption(): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const current = selected.settings.dropdown?.options ?? ['Option1'];
    this.updateSelectedFieldDropdownPatch({
      options: [...current, `Option${current.length + 1}`],
    });
  }

  removeSelectedDropdownOption(index: number): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const options = [...(selected.settings.dropdown?.options ?? [])];
    if (index < 0 || index >= options.length) {
      return;
    }

    options.splice(index, 1);
    const normalized = sanitizeOptionValues(options, 'Option1');
    const defaultOption = selected.settings.dropdown?.defaultOption;
    this.updateSelectedFieldDropdownPatch({
      options: normalized,
      defaultOption: defaultOption && normalized.includes(defaultOption) ? defaultOption : undefined,
    });
  }

  moveSelectedDropdownOption(index: number, direction: -1 | 1): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const options = [...(selected.settings.dropdown?.options ?? [])];
    const nextIndex = index + direction;
    if (index < 0 || index >= options.length || nextIndex < 0 || nextIndex >= options.length) {
      return;
    }

    const temp = options[index];
    options[index] = options[nextIndex]!;
    options[nextIndex] = temp!;
    this.updateSelectedFieldDropdownPatch({ options });
  }

  updateSelectedDropdownOption(index: number, value: string): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const options = [...(selected.settings.dropdown?.options ?? [])];
    if (index < 0 || index >= options.length) {
      return;
    }

    options[index] = value;
    this.updateSelectedFieldDropdownPatch({ options });
  }

  addSelectedCheckboxValue(): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return;
    }
    const current = this.checkboxGroupValuesModel();
    this.syncSelectedCheckboxOptionFields([...current, `Checkbox${current.length + 1}`], this.checkboxGroupDefaultValuesModel());
  }

  removeSelectedCheckboxValue(index: number): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return;
    }
    const current = [...this.checkboxGroupValuesModel()];
    if (index < 0 || index >= current.length) {
      return;
    }
    const removed = current[index]!;
    current.splice(index, 1);
    const defaults = this.checkboxGroupDefaultValuesModel().filter((entry) => entry !== removed);
    this.syncSelectedCheckboxOptionFields(current, defaults);
  }

  updateSelectedCheckboxValue(index: number, value: string): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return;
    }
    const current = [...this.checkboxGroupValuesModel()];
    if (index < 0 || index >= current.length) {
      return;
    }
    const previous = current[index]!;
    current[index] = value;
    const normalized = sanitizeOptionValues(current, 'Checkbox1');
    const defaults = [...this.checkboxGroupDefaultValuesModel()].map((entry) =>
      entry === previous ? (normalized[index] ?? entry) : entry,
    );
    this.syncSelectedCheckboxOptionFields(normalized, defaults);
  }

  toggleSelectedCheckboxDefault(value: string, checked: boolean): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return;
    }
    const current = this.checkboxGroupValuesModel();
    const defaults = new Set(this.checkboxGroupDefaultValuesModel());
    if (checked) {
      defaults.add(value);
    } else {
      defaults.delete(value);
    }
    this.syncSelectedCheckboxOptionFields(current, Array.from(defaults));
  }

  addSelectedRadioValue(): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return;
    }
    const current = this.radioGroupValuesModel();
    this.syncSelectedRadioOptionFields([...current, `Radio${current.length + 1}`], this.radioGroupDefaultValueModel());
  }

  removeSelectedRadioValue(index: number): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return;
    }
    const current = [...this.radioGroupValuesModel()];
    if (index < 0 || index >= current.length) {
      return;
    }
    const removed = current[index]!;
    current.splice(index, 1);
    const currentDefault = this.radioGroupDefaultValueModel();
    const nextDefault = currentDefault === removed ? undefined : currentDefault;
    this.syncSelectedRadioOptionFields(current, nextDefault);
  }

  updateSelectedRadioValue(index: number, value: string): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return;
    }
    const current = [...this.radioGroupValuesModel()];
    if (index < 0 || index >= current.length) {
      return;
    }
    const previous = current[index]!;
    current[index] = value;
    const normalized = sanitizeOptionValues(current, 'Radio1');
    const currentDefault = this.radioGroupDefaultValueModel();
    const nextDefault = currentDefault === previous ? normalized[index] : currentDefault;
    this.syncSelectedRadioOptionFields(normalized, nextDefault);
  }

  toggleSelectedRadioDefault(value: string, checked: boolean): void {
    if (!checked) {
      this.syncSelectedRadioOptionFields(this.radioGroupValuesModel(), undefined);
      return;
    }
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return;
    }
    this.syncSelectedRadioOptionFields(this.radioGroupValuesModel(), value);
  }

  checkboxGroupNameModel(): string {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return '';
    }
    return this.checkboxGroupSnapshot(selected).groupId;
  }

  checkboxGroupValuesModel(): string[] {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return [];
    }
    return this.checkboxGroupSnapshot(selected).values;
  }

  checkboxGroupDefaultValuesModel(): string[] {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return [];
    }
    return this.checkboxGroupSnapshot(selected).defaultValues;
  }

  radioGroupNameModel(): string {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return '';
    }
    return this.radioGroupSnapshot(selected).groupName;
  }

  radioGroupValuesModel(): string[] {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return [];
    }
    return this.radioGroupSnapshot(selected).values;
  }

  radioGroupDefaultValueModel(): string | undefined {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return undefined;
    }
    return this.radioGroupSnapshot(selected).defaultValue;
  }

  private ensureSelectedChoiceGroupConsistency(): void {
    const selected = this.selectedField;
    if (!selected || (selected.type !== 'checkbox' && selected.type !== 'radio')) {
      return;
    }

    this.ensureChoiceGroupConsistency(selected, selected.type);
  }

  private ensureChoiceGroupConsistency(anchor: TemplateField, type: 'checkbox' | 'radio'): void {
    if (type === 'checkbox') {
      const snapshot = this.checkboxGroupSnapshot(anchor);
      if (!this.checkboxGroupNeedsRepair(snapshot)) {
        return;
      }

      const baseSettings = normalizeFieldSettings(anchor.settings);
      this.applyChoiceGroupChanges(
        anchor,
        snapshot.fields,
        snapshot.values,
        (optionValue) => ({
          ...baseSettings,
          checkbox: {
            ...baseSettings.checkbox,
            optionGroupId: snapshot.groupId,
            values: snapshot.values,
            value: optionValue,
            checked: snapshot.defaultValues.includes(optionValue),
            defaultValues: snapshot.defaultValues,
          },
        }),
      );
      return;
    }

    const snapshot = this.radioGroupSnapshot(anchor);
    if (!this.radioGroupNeedsRepair(snapshot)) {
      return;
    }

    const baseSettings = normalizeFieldSettings(anchor.settings);
    this.applyChoiceGroupChanges(
      anchor,
      snapshot.fields,
      snapshot.values,
      (optionValue) => ({
        ...baseSettings,
        radio: {
          ...baseSettings.radio,
          groupName: snapshot.groupName,
          values: snapshot.values,
          value: optionValue,
          checked: snapshot.defaultValue === optionValue,
          defaultValue: snapshot.defaultValue,
        },
      }),
    );
  }

  private checkboxGroupSnapshot(
    anchor: TemplateField,
    preferredGroupId?: string,
  ): { groupId: string; fields: TemplateField[]; values: string[]; defaultValues: string[] } {
    const context = this.groupContextForField(anchor, 'checkbox', preferredGroupId);
    const values = this.canonicalChoiceGroupValues(context.fields, 'checkbox');
    const defaults = new Set<string>();

    for (const member of context.fields) {
      const settings = normalizeFieldSettings(member.settings).checkbox;
      const optionValue = trimOrUndefined(settings.value);
      if (settings.checked && optionValue && values.includes(optionValue)) {
        defaults.add(optionValue);
      }
      for (const defaultValue of sanitizeOptionValues(settings.defaultValues ?? [], '')) {
        if (values.includes(defaultValue)) {
          defaults.add(defaultValue);
        }
      }
    }

    return {
      groupId: context.groupKey,
      fields: context.fields,
      values,
      defaultValues: values.filter((value) => defaults.has(value)),
    };
  }

  private radioGroupSnapshot(
    anchor: TemplateField,
    preferredGroupName?: string,
  ): { groupName: string; fields: TemplateField[]; values: string[]; defaultValue: string | undefined } {
    const context = this.groupContextForField(anchor, 'radio', preferredGroupName);
    const values = this.canonicalChoiceGroupValues(context.fields, 'radio');

    let defaultValue = context.fields
      .map((member) => trimOrUndefined(normalizeFieldSettings(member.settings).radio.defaultValue))
      .find((value): value is string => !!value && values.includes(value));

    if (!defaultValue) {
      const checkedField = context.fields.find((member) => normalizeFieldSettings(member.settings).radio.checked);
      const checkedValue = checkedField ? this.choiceValue(checkedField, 'radio') : undefined;
      if (checkedValue && values.includes(checkedValue)) {
        defaultValue = checkedValue;
      }
    }

    return {
      groupName: context.groupKey,
      fields: context.fields,
      values,
      defaultValue,
    };
  }

  private canonicalChoiceGroupValues(fields: TemplateField[], type: 'checkbox' | 'radio'): string[] {
    const fallback = type === 'checkbox' ? 'Checkbox1' : 'Radio1';
    const valueLists = fields.map((member) => this.choiceValues(member, type));
    let baseList = valueLists[0] ? [...valueLists[0]] : [fallback];

    for (const candidate of valueLists) {
      if (candidate.length > baseList.length) {
        baseList = [...candidate];
      }
    }

    const ordered: string[] = [];
    const pushUnique = (value: string | undefined): void => {
      const trimmed = trimOrUndefined(value);
      if (!trimmed || ordered.includes(trimmed)) {
        return;
      }
      ordered.push(trimmed);
    };

    for (const value of baseList) {
      pushUnique(value);
    }
    for (const member of fields) {
      pushUnique(this.choiceValue(member, type));
    }
    for (const values of valueLists) {
      for (const value of values) {
        pushUnique(value);
      }
    }

    return sanitizeOptionValues(ordered, fallback);
  }

  private checkboxGroupNeedsRepair(snapshot: {
    groupId: string;
    fields: TemplateField[];
    values: string[];
    defaultValues: string[];
  }): boolean {
    if (snapshot.fields.length !== snapshot.values.length) {
      return true;
    }

    for (let index = 0; index < snapshot.values.length; index += 1) {
      const member = snapshot.fields[index];
      const expectedValue = snapshot.values[index]!;
      if (!member) {
        return true;
      }

      const settings = normalizeFieldSettings(member.settings).checkbox;
      const groupId = trimOrUndefined(settings.optionGroupId);
      const value = trimOrUndefined(settings.value);
      const defaults = sanitizeOptionValues(settings.defaultValues ?? [], '').filter((entry) => snapshot.values.includes(entry));
      const expectedChecked = snapshot.defaultValues.includes(expectedValue);

      if (groupId !== snapshot.groupId) {
        return true;
      }
      if (!this.areStringArraysEqual(settings.values ?? [], snapshot.values)) {
        return true;
      }
      if (value !== expectedValue) {
        return true;
      }
      if (!this.areStringArraysEqual(defaults, snapshot.defaultValues)) {
        return true;
      }
      if (settings.checked !== expectedChecked) {
        return true;
      }
    }

    return false;
  }

  private radioGroupNeedsRepair(snapshot: {
    groupName: string;
    fields: TemplateField[];
    values: string[];
    defaultValue: string | undefined;
  }): boolean {
    if (snapshot.fields.length !== snapshot.values.length) {
      return true;
    }

    for (let index = 0; index < snapshot.values.length; index += 1) {
      const member = snapshot.fields[index];
      const expectedValue = snapshot.values[index]!;
      if (!member) {
        return true;
      }

      const settings = normalizeFieldSettings(member.settings).radio;
      const groupName = trimOrUndefined(settings.groupName);
      const value = trimOrUndefined(settings.value);
      const defaultValue = trimOrUndefined(settings.defaultValue);
      const expectedChecked = snapshot.defaultValue === expectedValue;

      if (groupName !== snapshot.groupName) {
        return true;
      }
      if (!this.areStringArraysEqual(settings.values ?? [], snapshot.values)) {
        return true;
      }
      if (value !== expectedValue) {
        return true;
      }
      if (defaultValue !== snapshot.defaultValue) {
        return true;
      }
      if (settings.checked !== expectedChecked) {
        return true;
      }
    }

    return false;
  }

  private areStringArraysEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }
    for (let index = 0; index < left.length; index += 1) {
      if (left[index] !== right[index]) {
        return false;
      }
    }
    return true;
  }

  private syncSelectedCheckboxOptionFields(
    valuesInput: string[],
    defaultsInput: string[],
    groupOverride?: { sourceGroupId?: string; targetGroupId?: string },
  ): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'checkbox') {
      return;
    }
    const values = sanitizeOptionValues(valuesInput, 'Checkbox1');
    const defaults = new Set(sanitizeOptionValues(defaultsInput, ''));
    const context = this.groupContextForField(selected, 'checkbox', groupOverride?.sourceGroupId);
    const sourceGroupId = context.groupKey;
    const groupId = groupOverride?.targetGroupId ?? sourceGroupId;
    const groupFields = context.fields;
    this.applyChoiceGroupChanges(
      selected,
      groupFields,
      values,
      (optionValue, index) => {
        const checked = defaults.has(optionValue);
        return {
          ...normalizeFieldSettings(selected.settings),
          checkbox: {
            ...normalizeFieldSettings(selected.settings).checkbox,
            optionGroupId: groupId,
            values,
            value: optionValue,
            checked,
            defaultValues: values.filter((entry) => defaults.has(entry)),
          },
        };
      },
    );
  }

  private syncSelectedRadioOptionFields(
    valuesInput: string[],
    defaultValueInput?: string,
    groupOverride?: { sourceGroupName?: string; targetGroupName?: string },
  ): void {
    const selected = this.selectedField;
    if (!selected || selected.type !== 'radio') {
      return;
    }
    const values = sanitizeOptionValues(valuesInput, 'Radio1');
    const context = this.groupContextForField(selected, 'radio', groupOverride?.sourceGroupName);
    const sourceGroupName = context.groupKey;
    const groupName = groupOverride?.targetGroupName ?? sourceGroupName;
    const normalizedDefault = trimOrUndefined(defaultValueInput);
    const defaultValue = normalizedDefault && values.includes(normalizedDefault) ? normalizedDefault : undefined;
    const groupFields = context.fields;
    this.applyChoiceGroupChanges(
      selected,
      groupFields,
      values,
      (optionValue, index) => ({
        ...normalizeFieldSettings(selected.settings),
        radio: {
          ...normalizeFieldSettings(selected.settings).radio,
          groupName,
          values,
          value: optionValue,
          checked: defaultValue === optionValue,
          defaultValue,
        },
      }),
    );
  }

  private checkboxGroupFields(anchor: TemplateField, groupId?: string): TemplateField[] {
    return this.groupContextForField(anchor, 'checkbox', groupId).fields;
  }

  private resolveCheckboxGroupId(anchor: TemplateField): string {
    return this.groupContextForField(anchor, 'checkbox').groupKey;
  }

  private resolveRadioGroupName(anchor: TemplateField): string {
    return this.groupContextForField(anchor, 'radio').groupKey;
  }

  private radioGroupFields(anchor: TemplateField, groupName?: string): TemplateField[] {
    return this.groupContextForField(anchor, 'radio', groupName).fields;
  }

  private groupContextForField(
    anchor: TemplateField,
    type: 'checkbox' | 'radio',
    preferredGroupKey?: string,
  ): { groupKey: string; fields: TemplateField[] } {
    const candidates = this.choiceFieldsOnPageForRole(anchor, type);
    const groupKey = trimOrUndefined(preferredGroupKey) || this.resolveGroupKey(anchor, type, candidates);
    const anchorExplicitGroupKey = this.choiceExplicitGroupKey(anchor, type);
    const anchorHasStableGroupKey = !!anchorExplicitGroupKey && !this.isLegacyAutoGroupKey(anchorExplicitGroupKey);
    const grouped = new Map<string, TemplateField>();

    if (anchorHasStableGroupKey) {
      for (const field of candidates) {
        const explicit = this.choiceExplicitGroupKey(field, type);
        if (explicit === anchorExplicitGroupKey) {
          grouped.set(field.id, field);
        }
      }
    }

    for (const field of candidates) {
      const explicit = this.choiceExplicitGroupKey(field, type);
      if (explicit === groupKey) {
        grouped.set(field.id, field);
      }
    }

    for (const field of candidates) {
      const explicit = this.choiceExplicitGroupKey(field, type);
      if ((!explicit || this.isLegacyAutoGroupKey(explicit)) && this.hasSharedChoiceProfile(anchor, field, type)) {
        grouped.set(field.id, field);
      }
    }

    grouped.set(anchor.id, anchor);

    return {
      groupKey,
      fields: [...grouped.values()].sort((a, b) => (a.rect.y === b.rect.y ? a.rect.x - b.rect.x : a.rect.y - b.rect.y)),
    };
  }

  private resolveGroupKey(anchor: TemplateField, type: 'checkbox' | 'radio', candidates: TemplateField[]): string {
    const explicit = this.choiceExplicitGroupKey(anchor, type);
    if (explicit && !this.isLegacyAutoGroupKey(explicit)) {
      return explicit;
    }

    const siblingKey = candidates
      .filter((field) => field.id !== anchor.id)
      .filter((field) => this.hasSharedChoiceProfile(anchor, field, type))
      .map((field) => this.choiceExplicitGroupKey(field, type))
      .find((value) => !!value && !this.isLegacyAutoGroupKey(value));

    if (siblingKey) {
      return siblingKey;
    }

    const anchorFieldKey = trimOrUndefined(anchor.fieldKey);
    if (anchorFieldKey) {
      return anchorFieldKey;
    }

    return this.choiceFallbackGroupKey(anchor, type, candidates);
  }

  private choiceFallbackGroupKey(anchor: TemplateField, type: 'checkbox' | 'radio', candidates: TemplateField[]): string {
    const prefix = type === 'checkbox' ? 'CheckboxGroup' : 'RadioGroup';
    const ordered = [...candidates].sort((a, b) => (a.rect.y === b.rect.y ? a.rect.x - b.rect.x : a.rect.y - b.rect.y));
    const index = ordered.findIndex((field) => field.id === anchor.id);
    if (index >= 0) {
      return `${prefix}${index + 1}`;
    }
    return `${prefix}1`;
  }

  private choiceFieldsOnPageForRole(anchor: TemplateField, type: 'checkbox' | 'radio'): TemplateField[] {
    return this.draft.fields.filter((field) => field.type === type && field.page === anchor.page && field.role === anchor.role);
  }

  private choiceExplicitGroupKey(field: TemplateField, type: 'checkbox' | 'radio'): string | undefined {
    if (type === 'checkbox') {
      return trimOrUndefined(normalizeFieldSettings(field.settings).checkbox.optionGroupId);
    }
    return trimOrUndefined(normalizeFieldSettings(field.settings).radio.groupName);
  }

  private choiceValues(field: TemplateField, type: 'checkbox' | 'radio'): string[] {
    const settings = normalizeFieldSettings(field.settings);
    if (type === 'checkbox') {
      return sanitizeOptionValues(settings.checkbox.values ?? [settings.checkbox.value ?? 'Checkbox1'], 'Checkbox1');
    }
    return sanitizeOptionValues(settings.radio.values ?? [settings.radio.value ?? 'Radio1'], 'Radio1');
  }

  private choiceValue(field: TemplateField, type: 'checkbox' | 'radio'): string | undefined {
    const settings = normalizeFieldSettings(field.settings);
    if (type === 'checkbox') {
      return trimOrUndefined(settings.checkbox.value);
    }
    return trimOrUndefined(settings.radio.value);
  }

  private hasSharedChoiceProfile(anchor: TemplateField, candidate: TemplateField, type: 'checkbox' | 'radio'): boolean {
    if (anchor.id === candidate.id) {
      return true;
    }

    const anchorValues = this.choiceValues(anchor, type);
    const candidateValues = this.choiceValues(candidate, type);
    const anchorValue = this.choiceValue(anchor, type);
    const candidateValue = this.choiceValue(candidate, type);

    if (anchorValue && candidateValues.includes(anchorValue)) {
      return true;
    }
    if (candidateValue && anchorValues.includes(candidateValue)) {
      return true;
    }

    if (anchorValues.length === 0 || candidateValues.length === 0) {
      return false;
    }

    return anchorValues.join('\u0000') === candidateValues.join('\u0000');
  }

  private isLegacyAutoGroupKey(value: string | undefined): boolean {
    return !!value && LEGACY_AUTO_GROUP_KEY_PATTERN.test(value);
  }

  private applyChoiceGroupChanges(
    selected: TemplateField,
    existingGroup: TemplateField[],
    values: string[],
    makeSettings: (value: string, index: number) => TemplateFieldSettings,
  ): void {
    const anchor = existingGroup.find((field) => field.id === selected.id) ?? selected;
    const group = existingGroup.length > 0 ? existingGroup : [anchor];
    const groupIds = new Set(group.map((field) => field.id));
    const nextFields = this.draft.fields.filter((field) => !groupIds.has(field.id));
    const replacements: TemplateField[] = [];
    for (let index = 0; index < values.length; index += 1) {
      const optionValue = values[index]!;
      const current = group[index];
      const settings = makeSettings(optionValue, index);
      if (current) {
        replacements.push({
          ...current,
          rect: this.choiceRectFromSettings(current.rect, selected.page, selected.type, settings),
          settings,
        });
        continue;
      }
      const seedRect = this.findChoiceSiblingRect(anchor, index);
      if (!seedRect) {
        continue;
      }
      const created = this.createField(selected.type, selected.role, selected.page, seedRect);
      replacements.push({
        ...created,
        rect: this.choiceRectFromSettings(seedRect, selected.page, selected.type, settings),
        required: selected.required,
        label: selected.label,
        placeholder: selected.placeholder,
        settings,
      });
    }

    this.draft.fields = this.dedupeFieldsByIdKeepLast([...nextFields, ...replacements]);
    this.selectedFieldId = replacements.find((field) => field.id === selected.id)?.id ?? replacements[0]?.id ?? this.selectedFieldId;
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  private findChoiceSiblingRect(anchor: TemplateField, index: number): TemplateFieldRect | null {
    const gap = 0.004;
    const baseRect = { ...anchor.rect };
    const startY = baseRect.y + index * (baseRect.height + gap);
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const candidate = this.clampRectToPage({ ...baseRect, y: startY + attempt * (baseRect.height + gap) });
      if (!this.wouldOverlapField(null, anchor.page, candidate)) {
        return candidate;
      }
    }
    return null;
  }

  private choiceRectFromSettings(
    rect: TemplateFieldRect,
    page: number,
    type: TemplateFieldType,
    settings: TemplateFieldSettings,
  ): TemplateFieldRect {
    if (type !== 'checkbox' && type !== 'radio') {
      return rect;
    }
    const pageElement = this.resolvePageElement(page);
    const pageRect = pageElement?.getBoundingClientRect();
    if (!pageRect || pageRect.width <= 0 || pageRect.height <= 0) {
      return rect;
    }
    const sizePx = type === 'checkbox' ? settings.checkbox.size : settings.radio.size;
    if (!Number.isFinite(sizePx) || sizePx <= 0) {
      return rect;
    }
    const width = this.clamp(sizePx / pageRect.width, MIN_MEASURED_FIELD_WIDTH, MAX_MEASURED_FIELD_WIDTH);
    const height = this.clamp(sizePx / pageRect.height, MIN_MEASURED_FIELD_HEIGHT, MAX_MEASURED_FIELD_HEIGHT);
    return this.clampRectToPage({ ...rect, width, height });
  }

  updateSelectedFieldPosition(axis: 'x' | 'y', value: number): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const pageElement = this.viewerHost?.nativeElement.querySelector<HTMLElement>(
      `.page[data-page-number=\"${selected.page}\"]`,
    );
    if (!pageElement) {
      return;
    }

    const pageRect = pageElement.getBoundingClientRect();
    if (pageRect.width <= 0 || pageRect.height <= 0) {
      return;
    }

    const size = axis === 'x' ? pageRect.width : pageRect.height;
    const normalized = this.clamp((value || 0) / size, 0, 1 - (axis === 'x' ? selected.rect.width : selected.rect.height));
    const rect: TemplateFieldRect = {
      ...selected.rect,
      [axis]: normalized,
    };

    this.patchField(selected.id, { rect });
    this.cdr.markForCheck();
  }

  selectedFieldPositionPx(axis: 'x' | 'y'): number {
    const selected = this.selectedField;
    if (!selected) {
      return 0;
    }

    const pageElement = this.viewerHost?.nativeElement.querySelector<HTMLElement>(
      `.page[data-page-number=\"${selected.page}\"]`,
    );
    if (!pageElement) {
      return 0;
    }

    const pageRect = pageElement.getBoundingClientRect();
    const size = axis === 'x' ? pageRect.width : pageRect.height;
    const normalized = axis === 'x' ? selected.rect.x : selected.rect.y;
    return Math.round(normalized * size);
  }

  roleNameById(roleId: string): string | undefined {
    return this.draft.roles.find((role) => role.id === roleId)?.role || undefined;
  }

  fieldIconId(type: TemplateFieldType): string {
    return FIELD_ICON_IDS[type];
  }

  fieldLabel(type: TemplateFieldType): string {
    return FIELD_LABELS[type];
  }

  roleChipColor(roleId: string): string {
    this.syncRoleColorRegistry();
    return this.roleColorRegistry.get(roleId) ?? ROLE_COLOR_PALETTE[0];
  }

  fieldsCountForRole(roleId: string): number {
    return countFieldsForActor(this.draft.fields, roleId);
  }

  visibleFieldsForSummary(): TemplateField[] {
    return filterFieldsForSummary(this.draft.fields, this.previewRecipientFilter) as TemplateField[];
  }

  pageNumbers(): number[] {
    return Array.from({ length: Math.max(this.pageCount, 1) }, (_unused, index) => index + 1);
  }

  restartWalkthrough(): void {
    this.walkthroughVisible = true;
    this.walkthroughIndex = 0;
    this.cdr.markForCheck();
  }

  skipWalkthrough(): void {
    this.walkthroughVisible = false;
    this.markWalkthroughSeen();
    this.cdr.markForCheck();
  }

  nextWalkthroughStep(): void {
    if (this.walkthroughIndex >= this.walkthroughSteps.length - 1) {
      this.walkthroughVisible = false;
      this.markWalkthroughSeen();
      this.cdr.markForCheck();
      return;
    }

    this.walkthroughIndex += 1;
    this.cdr.markForCheck();
  }

  saveTemplate(): void {
    if (this.savingTemplate || this.loadingTemplateForEdit || this.publicTemplateConfigInitializing) {
      return;
    }

    if (this.isPublicTemplateConfigureMode && this.publicTemplateConfigToken) {
      this.stepTwoSubmitted = true;
      this.stepTwoError = '';
      this.publicTemplateSaveMessage = '';

      const validationError = this.validateStepTwo();
      if (validationError) {
        this.setStepTwoError(validationError);
        return;
      }

      this.savingTemplate = true;
      this.api
        .savePublicTemplateConfigFields(this.publicTemplateConfigToken, {
          fields: this.buildNormalizedFieldsPayload(),
        })
        .pipe(
          finalize(() => {
            this.savingTemplate = false;
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
          next: (response) => {
            this.feedback.clearError();
            this.stepTwoError = '';
            this.publicTemplateId = response.templateId;
            this.publicTemplateSaveMessage = 'Fields saved successfully.';
            this.postPublicTemplateConfigSaved(response.templateId);
            this.cdr.markForCheck();
          },
          error: (error: unknown) => {
            const normalized = normalizeApiError(error, {
              fallbackMessage: 'Failed to save template fields',
            });
            this.stepTwoError = normalized.message;
            this.feedback.showError({ ...normalized });
            this.cdr.markForCheck();
          },
        });
      return;
    }

    this.stepOneSubmitted = true;
    this.stepTwoSubmitted = true;
    this.stepTwoError = '';

    if (this.isStepOneInvalid()) {
      this.currentStep = 1;
      this.cdr.markForCheck();
      return;
    }

    const validationError = this.validateStepTwo();
    if (validationError) {
      this.setStepTwoError(validationError);
      return;
    }

    const selectedFile = this.draft.selectedFile;
    if (!selectedFile) {
      this.currentStep = 1;
      this.fileError = 'A PDF file is required.';
      this.cdr.markForCheck();
      return;
    }

    this.savingTemplate = true;

    this.persistTemplate(selectedFile)
      .pipe(
        finalize(() => {
          this.savingTemplate = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (template: TemplateSummary) => {
          this.feedback.clearError();
          this.stepTwoError = '';
          this.lastSavedTemplateName = template.name;
          this.currentSourceFile = { ...template.sourceFile };
          this.sourceFileReplaced = false;
          this.showSaveSuccessModal = true;
          this.markWalkthroughSeen();
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: this.isEditMode ? 'Failed to update template' : 'Failed to save template',
          });
          this.stepTwoError = normalized.message;
          this.feedback.showError({ ...normalized });
          this.cdr.markForCheck();
        },
      });
  }

  dismissPublicTemplateSaveMessage(): void {
    this.publicTemplateSaveMessage = '';
    this.cdr.markForCheck();
  }

  closeSavedModal(): void {
    this.showSaveSuccessModal = false;
    void this.router.navigate(['/templates']);
  }

  private loadTemplateForEdit(templateId: string): void {
    this.loadingTemplateForEdit = true;
    this.editLoadError = '';

    this.api
      .getTemplate(templateId)
      .pipe(
        switchMap((template) =>
          forkJoin({
            template: of(template),
            pdfBlob: this.api.downloadPdf(template.sourceFile.fileId),
          }),
        ),
        finalize(() => {
          this.loadingTemplateForEdit = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ template, pdfBlob }) => {
          this.feedback.clearError();
          const removedDeprecatedFields = this.hydrateDraftFromTemplate(template, pdfBlob);
          if (removedDeprecatedFields > 0) {
            this.feedback.showError({
              kind: 'client',
              message: `${removedDeprecatedFields} deprecated field(s) were removed: Attachment, Drawing, Formula.`,
            });
          }
          this.editLoadError = '';
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to load template for editing',
          });
          this.editLoadError = normalized.message;
          this.feedback.showError({
            ...normalized,
            actionLabel: 'Retry',
            action: () => this.loadTemplateForEdit(templateId),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private loadPublicTemplateConfigSession(token: string): void {
    this.publicTemplateConfigInitializing = true;
    this.editLoadError = '';
    this.publicTemplateSaveMessage = '';

    this.api
      .getPublicTemplateConfigSession(token)
      .pipe(
        switchMap((session) =>
          forkJoin({
            session: of(session),
            pdfBlob: this.api.getPublicTemplateConfigPdf(token),
          }),
        ),
        finalize(() => {
          this.publicTemplateConfigInitializing = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ session, pdfBlob }) => {
          this.feedback.clearError();
          this.hydrateDraftFromPublicTemplateSession(session, pdfBlob);
          this.editLoadError = '';
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: 'Failed to load template configuration session',
          });
          this.editLoadError = normalized.message;
          this.feedback.showError({
            ...normalized,
            actionLabel: 'Retry',
            action: () => this.loadPublicTemplateConfigSession(token),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private hydrateDraftFromPublicTemplateSession(
    session: import('../../models/template.models').PublicTemplateConfigSessionResponse,
    pdfBlob: Blob,
  ): void {
    const updatedAt = new Date().toISOString();
    this.publicTemplateId = session.template.id;
    this.hydrateDraftFromTemplate(
      {
        _id: session.template.id,
        status: session.template.status ?? 'draft',
        name: session.template.name,
        description: session.template.description,
        sourceFile: {
          fileId: session.template.sourceFile.fileId,
          s3Key: '',
          bucket: '',
          originalName: session.template.sourceFile.originalName,
          sizeBytes: pdfBlob.size,
          pageCount: session.template.sourceFile.pageCount,
          uploadedAt: updatedAt,
        },
        fields: session.fields,
        signingOrderEnabled: session.template.signingOrderEnabled,
        roles: session.roles,
        ccEmails: session.template.ccEmails,
        document: session.template.document,
        tags: session.template.tags,
        usageSettings: session.template.usageSettings,
        documentSettings: session.template.documentSettings,
        createdAt: updatedAt,
        updatedAt,
      },
      pdfBlob,
    );
    this.currentStep = 2;
    this.stepTwoMode = 'configure';
    this.walkthroughVisible = false;
    this.stepOneSubmitted = false;
    this.stepTwoSubmitted = false;
    this.stepTwoError = '';
    this.selectedFieldId = null;
    this.activeRoleId = this.draft.roles[0]?.id ?? '';
    this.previewRecipientFilter = 'all';
    this.beginPdfLoadAttempt('public-template-config');
    this.tracePdfEvent('viewer-bootstrap-rendered', { sourcePresent: this.viewerPdfSource !== null });
  }

  private hydrateDraftFromTemplate(template: TemplateSummary, pdfBlob: Blob): number {
    const lastModified = Number.isNaN(Date.parse(template.updatedAt))
      ? Date.now()
      : new Date(template.updatedAt).getTime();
    const pdfFile = new File([pdfBlob], template.sourceFile.originalName, {
      type: 'application/pdf',
      lastModified,
    });

    const draftRoles: TemplateCreateRoleDraft[] = template.roles.map((role) => ({
      id: role.id,
      role: role.name,
      defaultRecipientName: role.defaultRecipientName ?? '',
      defaultRecipientEmail: role.defaultRecipientEmail ?? '',
    }));

    this.draft = {
      templateTitle: template.name,
      templateDescription: template.description ?? '',
      selectedFile: pdfFile,
      cc: template.ccEmails.join(', '),
      documentTitle: template.document.title,
      documentMessage: template.document.message ?? '',
      tags: template.tags.join(', '),
      signingOrderEnabled: template.signingOrderEnabled,
      roles: draftRoles.length > 0 ? draftRoles : [createEmptyRole()],
      fields: [],
      usageSettings: { ...template.usageSettings },
      documentSettings: this.normalizedDocumentSettings(template.documentSettings),
    };
    this.ensureBrandSelection(this.draft.documentSettings);

    this.currentSourceFile = { ...template.sourceFile };
    this.sourceFileReplaced = false;
    this.fileError = '';
    this.stepTwoError = '';
    this.stepOneSubmitted = false;
    this.stepTwoSubmitted = false;
    this.currentStep = 1;
    this.showSaveSuccessModal = false;
    this.lastSavedTemplateName = template.name;

    this.clearWorkingPdfState();
    if (!this.ensurePdfSources()) {
      this.fileError = 'File preview could not be prepared. Please reselect the PDF.';
    }

    const rawFields = template.fields as Array<TemplateField | (Omit<TemplateField, 'type'> & { type: string })>;
    const fieldKeyCounts = new Map<string, number>();
    let removedDeprecatedFields = 0;
    const hydratedFields = rawFields.flatMap((field) => {
      const normalizedType = this.normalizeTemplateFieldType(field.type);
      if (!normalizedType) {
        removedDeprecatedFields += 1;
        return [];
      }

      const prefix = defaultFieldKeyPrefix(normalizedType);
      const nextCount = (fieldKeyCounts.get(prefix) ?? 0) + 1;
      fieldKeyCounts.set(prefix, nextCount);

      return [{
        ...field,
        type: normalizedType,
        fieldKey: trimOrUndefined(field.fieldKey) ?? `${prefix}${nextCount}`,
        rect: { ...field.rect },
        settings: normalizeFieldSettingsForType(normalizedType, field.settings),
      }];
    });
    this.draft.fields = this.dedupeFieldsByIdKeepLast(hydratedFields);
    this.normalizeChoiceGroupMetadata();
    this.activeRoleId = this.draft.roles[0]?.id ?? '';
    this.previewRecipientFilter = 'all';
    this.syncRoleColorRegistry();
    this.scheduleOverlaySync();
    return removedDeprecatedFields;
  }

  private buildNormalizedFieldsPayload(): TemplateField[] {
    this.normalizeChoiceGroupMetadata();
    this.draft.fields = this.dedupeFieldsByIdKeepLast(this.draft.fields);

    return this.draft.fields.map((field) => ({
      ...field,
      fieldKey: this.normalizedFieldKey(field),
      label: field.label?.trim() || undefined,
      placeholder: field.placeholder?.trim() || undefined,
      settings: normalizeFieldSettingsForType(field.type, field.settings),
    }));
  }

  private persistTemplate(selectedFile: File): Observable<TemplateSummary> {
    const templateId = this.templateIdForEdit;
    if (!this.isEditMode || !templateId) {
      return this.api.uploadPdf(selectedFile).pipe(
        switchMap((uploadedFile) => {
          const payload = this.buildTemplatePayload(uploadedFile);
          return this.api.createTemplate(payload);
        }),
      );
    }

    if (!this.sourceFileReplaced && this.currentSourceFile) {
      const payload = this.buildTemplatePayload(this.currentSourceFile);
      return this.api.updateTemplate(templateId, payload);
    }

    return this.api.uploadPdf(selectedFile).pipe(
      switchMap((uploadedFile) => {
        const payload = this.buildTemplatePayload(uploadedFile);
        return this.api.updateTemplate(templateId, payload);
      }),
    );
  }

  private validateStepTwo(): string | null {
    return validateFieldConfiguratorStep(this.draft.fields, this.fieldConfiguratorActors, this.pageCount, {
      emptyFields: 'Add at least one field before saving.',
      missingActor: (actor) => `Add at least one field for role "${actor.label}".`,
      unknownActor: 'One or more fields are assigned to an unknown role.',
      outOfBounds: (field) => `Field on page ${field.page} is out of bounds.`,
    });
  }

  private buildTemplatePayload(sourceFile: TemplatePayload['sourceFile']): TemplatePayload {
    this.normalizeChoiceGroupMetadata();
    this.draft.fields = this.dedupeFieldsByIdKeepLast(this.draft.fields);

    const roles: TemplateRole[] = this.draft.roles.map((role, index) => ({
      id: role.id,
      name: role.role.trim(),
      defaultRecipientName: role.defaultRecipientName.trim() || undefined,
      defaultRecipientEmail: role.defaultRecipientEmail.trim() || undefined,
      order: index + 1,
    }));

    return {
      name: this.draft.templateTitle.trim(),
      description: this.draft.templateDescription.trim() || undefined,
      sourceFile,
      fields: this.buildNormalizedFieldsPayload(),
      signingOrderEnabled: this.draft.signingOrderEnabled,
      roles,
      ccEmails: this.parseCommaSeparatedValues(this.draft.cc),
      document: {
        title: this.draft.documentTitle.trim(),
        message: this.draft.documentMessage.trim() || undefined,
      },
      tags: this.parseCommaSeparatedValues(this.draft.tags),
      usageSettings: { ...this.draft.usageSettings },
      documentSettings: this.normalizedDocumentSettings(this.draft.documentSettings),
    };
  }

  private postPublicTemplateConfigSaved(templateId: string): void {
    if (typeof window === 'undefined' || !window.parent) {
      return;
    }

    window.parent.postMessage(
      {
        type: 'template-config.saved',
        templateId,
      },
      '*',
    );
  }

  onBrandSelectionChange(brandId: string): void {
    this.applyBrandIdToSettings(this.draft.documentSettings, brandId);
  }

  private parseCommaSeparatedValues(value: string): string[] {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry, index, array) => entry.length > 0 && array.indexOf(entry) === index);
  }

  private loadBrands(): void {
    this.brandsLoading = true;
    const request$ =
      this.isPublicTemplateConfigureMode && this.publicTemplateConfigToken
        ? this.api.getPublicTemplateConfigBrands(this.publicTemplateConfigToken)
        : this.brandApi.listBrands();

    request$
      .pipe(
        finalize(() => {
          this.brandsLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.brands = response.items;
          this.ensureBrandSelection(this.draft.documentSettings);
          this.cdr.markForCheck();
        },
        error: () => {
          this.brands = [];
          this.cdr.markForCheck();
        },
      });
  }

  private defaultBrandId(): string | undefined {
    return this.brands.find((brand) => brand.isDefault)?.brandId ?? this.brands[0]?.brandId;
  }

  private applyBrandIdToSettings(settings: TemplateDocumentSettings, brandId: string | undefined): void {
    const normalized = brandId?.trim();
    const selectedBrand = normalized ? this.brands.find((brand) => brand.brandId === normalized) : undefined;
    settings.brandId = selectedBrand?.brandId;
    settings.brandName = selectedBrand?.name;
    settings.brand = undefined;
  }

  private ensureBrandSelection(settings: TemplateDocumentSettings): void {
    if (settings.brandId?.trim()) {
      this.applyBrandIdToSettings(settings, settings.brandId);
      return;
    }

    const fallbackBrandId = this.defaultBrandId();
    if (fallbackBrandId) {
      this.applyBrandIdToSettings(settings, fallbackBrandId);
      return;
    }

    settings.brandId = undefined;
    settings.brandName = settings.brandName?.trim() || settings.brand?.trim() || undefined;
    settings.brand = undefined;
  }

  private normalizedDocumentSettings(settings: TemplateDocumentSettings): TemplateDocumentSettings {
    return {
      brandId: settings.brandId?.trim() || undefined,
      brandName: settings.brandName?.trim() || settings.brand?.trim() || undefined,
      allowPrintAndSign: settings.allowPrintAndSign,
    };
  }

  private validateFieldSettings(field: TemplateField): string | null {
    const settings = normalizeFieldSettings(field.settings);

    if (settings.common.tabIndexMode === 'custom' && (!settings.common.tabIndex || settings.common.tabIndex < 1)) {
      return `Field "${FIELD_LABELS[field.type]}" has an invalid custom tab index.`;
    }

    if (field.type === 'dropdown') {
      const options = settings.dropdown?.options ?? [];
      if (options.length === 0) {
        return 'Dropdown options must include at least one value.';
      }

      const defaultOption = settings.dropdown?.defaultOption;
      if (defaultOption && !options.includes(defaultOption)) {
        return 'Dropdown default option must match one of the options.';
      }
    }

    if (field.type === 'checkbox') {
      const values = sanitizeOptionValues(settings.checkbox?.values ?? [settings.checkbox?.value ?? ''], 'Checkbox1');
      if (values.length === 0) {
        return 'Checkbox values must include at least one value.';
      }
    }

    if (field.type === 'radio') {
      const values = sanitizeOptionValues(settings.radio?.values ?? [settings.radio?.value ?? ''], 'Radio1');
      if (values.length === 0) {
        return 'Radio values must include at least one value.';
      }
    }

    return null;
  }

  private choiceGroupFieldIds(field: TemplateField): string[] {
    if (field.type === 'checkbox') {
      return this.groupContextForField(field, 'checkbox').fields.map((entry) => entry.id);
    }
    if (field.type === 'radio') {
      return this.groupContextForField(field, 'radio').fields.map((entry) => entry.id);
    }
    return [field.id];
  }

  private choiceGroupKey(field: TemplateField): string {
    if (field.type === 'checkbox') {
      return `checkbox:${this.resolveCheckboxGroupId(field)}`;
    }
    if (field.type === 'radio') {
      return `radio:${this.resolveRadioGroupName(field)}`;
    }
    return field.id;
  }

  private validateChoiceGroup(anchor: TemplateField): string | null {
    if (anchor.type !== 'checkbox' && anchor.type !== 'radio') {
      return null;
    }
    const groupIds = new Set(this.choiceGroupFieldIds(anchor));
    const groupFields = this.draft.fields.filter((field) => groupIds.has(field.id));
    if (groupFields.length <= 1) {
      return null;
    }
    const first = groupFields[0]!;
    const sameRole = groupFields.every((field) => field.role === first.role);
    if (!sameRole) {
      return `${FIELD_LABELS[anchor.type]} options in the same group must have the same recipient role.`;
    }
    const samePage = groupFields.every((field) => field.page === first.page);
    if (!samePage) {
      return `${FIELD_LABELS[anchor.type]} options in the same group must stay on the same page.`;
    }
    const sameRequired = groupFields.every((field) => field.required === first.required);
    if (!sameRequired) {
      return `${FIELD_LABELS[anchor.type]} options in the same group must share the same required setting.`;
    }
    if (anchor.type === 'radio') {
      const groupName = this.resolveRadioGroupName(anchor);
      if (!groupName) {
        return 'Radio group name is required for grouped radio options.';
      }
    }
    return null;
  }

  private normalizeChoiceGroupMetadata(): void {
    this.draft.fields = this.dedupeFieldsByIdKeepLast(this.draft.fields);

    const sortedChoices = this.draft.fields
      .filter((field) => field.type === 'checkbox' || field.type === 'radio')
      .sort((a, b) => {
        if (a.page !== b.page) {
          return a.page - b.page;
        }
        if (a.role !== b.role) {
          return a.role.localeCompare(b.role);
        }
        return a.rect.y === b.rect.y ? a.rect.x - b.rect.x : a.rect.y - b.rect.y;
      });

    if (sortedChoices.length === 0) {
      return;
    }

    const processed = new Set<string>();
    const updates = new Map<string, TemplateField>();

    for (const field of sortedChoices) {
      if (processed.has(field.id)) {
        continue;
      }

      const type = field.type as 'checkbox' | 'radio';
      const context = this.groupContextForField(field, type);
      const targetGroupKey = context.groupKey;

      for (const member of context.fields) {
        processed.add(member.id);
        const current = updates.get(member.id) ?? member;
        const settings = normalizeFieldSettings(current.settings);
        if (type === 'checkbox') {
          const explicit = trimOrUndefined(settings.checkbox.optionGroupId);
          if (explicit && !this.isLegacyAutoGroupKey(explicit)) {
            continue;
          }
          updates.set(member.id, {
            ...current,
            settings: {
              ...settings,
              checkbox: {
                ...settings.checkbox,
                optionGroupId: targetGroupKey,
              },
            },
          });
          continue;
        }

        const explicit = trimOrUndefined(settings.radio.groupName);
        if (explicit && !this.isLegacyAutoGroupKey(explicit)) {
          continue;
        }
        updates.set(member.id, {
          ...current,
          settings: {
            ...settings,
            radio: {
              ...settings.radio,
              groupName: targetGroupKey,
            },
          },
        });
      }
    }

    if (updates.size === 0) {
      return;
    }

    this.draft.fields = this.draft.fields.map((field) => updates.get(field.id) ?? field);
    this.draft.fields = this.dedupeFieldsByIdKeepLast(this.draft.fields);
  }

  private dedupeFieldsByIdKeepLast(fields: TemplateField[]): TemplateField[] {
    const lastIndexById = new Map<string, number>();
    fields.forEach((field, index) => {
      lastIndexById.set(field.id, index);
    });
    return fields.filter((field, index) => lastIndexById.get(field.id) === index);
  }

  private maybeShowWalkthrough(): void {
    if (this.isWalkthroughSeen()) {
      this.walkthroughVisible = false;
      return;
    }

    this.walkthroughVisible = true;
    this.walkthroughIndex = 0;
  }

  private isWalkthroughSeen(): boolean {
    return localStorage.getItem(this.walkthroughKey()) === '1';
  }

  private markWalkthroughSeen(): void {
    localStorage.setItem(this.walkthroughKey(), '1');
  }

  private walkthroughKey(): string {
    const userId = this.authSession.currentUser()?.id ?? 'anonymous';
    return `${WALKTHROUGH_KEY_PREFIX}:${userId}`;
  }

  private ensurePdfSources(): boolean {
    const selectedFile = this.draft.selectedFile;
    if (!selectedFile) {
      return false;
    }

    if (!this.pdfObjectUrl) {
      try {
        this.pdfObjectUrl = URL.createObjectURL(selectedFile);
        this.viewerMounted = true;
        this.tracePdfEvent('source-created', {
          fileName: selectedFile.name,
          sizeBytes: selectedFile.size,
          type: selectedFile.type || 'unknown',
        });
      } catch (error) {
        this.tracePdfEvent('pdfSourceCreateFailed', error);
        return false;
      }
    }

    return this.viewerPdfSource !== null;
  }

  private clearWorkingPdfState(): void {
    this.clearFieldInteractionState();
    this.draft.fields = [];
    this.selectedFieldId = null;
    this.pageCount = 1;
    this.activePage = 1;
    this.stepTwoMode = 'configure';
    this.walkthroughVisible = false;
    this.pdfLoading = false;
    this.pdfLoaded = false;
    this.pdfLoadError = '';
    this.pdfLoadErrorDetail = '';
    this.pdfBootstrapError = '';
    this.viewerMounted = false;
    this.showViewerInstance = true;
    this.pdfEventCountThisAttempt = 0;
    this.pdfBootstrapRetryCount = 0;
    this.viewerRenderNonce = 0;
    this.dragFieldType = null;
    this.clearPdfStuckSnapshotTimer();
    this.clearPdfBootstrapWatchdogTimer();
    this.clearPdfSources();
    this.tracePdfEvent('clearWorkingPdfState');
    this.scheduleOverlaySync();
  }

  private clearPdfSources(): void {
    if (this.pdfObjectUrl) {
      this.tracePdfEvent('source-revoked');
      URL.revokeObjectURL(this.pdfObjectUrl);
      this.pdfObjectUrl = null;
      this.viewerMounted = false;
    }
  }

  private isStepOneInvalid(): boolean {
    if (this.draft.templateTitle.trim().length === 0) {
      return true;
    }

    if (this.draft.selectedFile === null) {
      return true;
    }

    if (this.draft.roles.some((role) => role.role.trim().length === 0)) {
      return true;
    }

    if (this.draft.documentTitle.trim().length === 0) {
      return true;
    }

    return false;
  }

  private isRectInvalid(rect: TemplateFieldRect): boolean {
    if (rect.x < 0 || rect.x > 1 || rect.y < 0 || rect.y > 1) {
      return true;
    }

    if (rect.width <= 0 || rect.width > 1 || rect.height <= 0 || rect.height > 1) {
      return true;
    }

    if (rect.x + rect.width > 1 || rect.y + rect.height > 1) {
      return true;
    }

    return false;
  }

  private isPdfFile(file: File): boolean {
    if (file.type === 'application/pdf') {
      return true;
    }

    return file.name.toLowerCase().endsWith('.pdf');
  }

  private extractPagesCount(event: unknown): number {
    if (!event || typeof event !== 'object') {
      return 0;
    }

    const direct = (event as { pagesCount?: unknown }).pagesCount;
    if (typeof direct === 'number' && Number.isInteger(direct) && direct > 0) {
      return direct;
    }

    const nested = (event as { pages?: unknown }).pages;
    if (typeof nested === 'number' && Number.isInteger(nested) && nested > 0) {
      return nested;
    }

    return 0;
  }

  private extractPageChange(event: unknown): number {
    if (typeof event === 'number' && Number.isInteger(event)) {
      return event;
    }

    if (!event || typeof event !== 'object') {
      return 0;
    }

    const eventNumber = (event as { pageNumber?: unknown }).pageNumber;
    if (typeof eventNumber === 'number' && Number.isInteger(eventNumber)) {
      return eventNumber;
    }

    const currentPageNumber = (event as { currentPageNumber?: unknown }).currentPageNumber;
    if (typeof currentPageNumber === 'number' && Number.isInteger(currentPageNumber)) {
      return currentPageNumber;
    }

    return 0;
  }

  private setStepTwoError(message: string): void {
    this.stepTwoError = message;
    this.feedback.showError({
      kind: 'client',
      message,
    });
    this.cdr.markForCheck();
  }

  private defaultRectByType(type: TemplateFieldType): TemplateFieldRect {
    if (type === 'signature') {
      return { x: 0.12, y: 0.12, width: 0.28, height: COMPACT_FIELD_HEIGHT };
    }

    if (type === 'image') {
      return { x: 0.12, y: 0.26, width: 0.24, height: COMPACT_FIELD_HEIGHT };
    }

    if (type === 'date_signed' || type === 'editable_date') {
      const dateText = this.currentUserDateSettings().dateFormat;
      return {
        x: 0.12,
        y: 0.26,
        width: textWidthToRect(dateText, { min: 0.12, max: 0.2, base: 0.04, perChar: 0.01 }),
        height: COMPACT_FIELD_HEIGHT,
      };
    }

    if (type === 'checkbox' || type === 'radio') {
      const optionText =
        type === 'checkbox'
          ? (createDefaultCheckboxSettings().value ?? 'Checkbox1')
          : (createDefaultRadioSettings().value ?? 'Radio1');
      return {
        x: 0.12,
        y: 0.26,
        width: textWidthToRect(optionText, { min: 0.065, max: 0.14, base: 0.03, perChar: 0.0075 }),
        height: COMPACT_FIELD_HEIGHT,
      };
    }

    const labelText = FIELD_LABELS[type];
    const width =
      type === 'dropdown'
        ? textWidthToRect(labelText, { min: 0.105, max: 0.22, base: 0.048, perChar: 0.0084 })
        : textWidthToRect(labelText, { min: 0.095, max: 0.2, base: 0.04, perChar: 0.008 });

    return { x: 0.12, y: 0.36, width, height: COMPACT_FIELD_HEIGHT };
  }

  private resolvePageElement(pageNumber: number): HTMLElement | null {
    if (!Number.isInteger(pageNumber) || pageNumber < 1) {
      return null;
    }

    return this.viewerHost?.nativeElement.querySelector<HTMLElement>(`.page[data-page-number="${pageNumber}"]`) ?? null;
  }

  private computeFitRectForNewField(
    type: TemplateFieldType,
    roleId: string,
    page: number,
    pageElement: HTMLElement | null,
  ): TemplateFieldRect | null {
    if (!pageElement) {
      return this.defaultRectByType(type);
    }

    const pageRect = pageElement.getBoundingClientRect();
    if (pageRect.width <= 0 || pageRect.height <= 0) {
      return this.defaultRectByType(type);
    }

    const probeField: TemplateField = {
      id: 'field-measure',
      fieldKey: 'FieldMeasure',
      type,
      role: roleId,
      page,
      rect: { x: 0, y: 0, width: 0.1, height: COMPACT_FIELD_HEIGHT },
      required: true,
      label: FIELD_LABELS[type],
      placeholder: undefined,
      settings: this.createFieldSettingsWithUserDefaults(),
    };

    return this.measureFieldRectFromRenderedContent(probeField, pageElement) ?? this.defaultRectByType(type);
  }

  private measureFieldRectFromRenderedContent(field: TemplateField, pageElement: HTMLElement | null): TemplateFieldRect | null {
    if (!pageElement) {
      return null;
    }

    const pageRect = pageElement.getBoundingClientRect();
    if (pageRect.width <= 0 || pageRect.height <= 0) {
      return null;
    }

    const probe = document.createElement('div');
    probe.className = `template-field template-field--${field.type} template-field--measure`;
    probe.style.position = 'absolute';
    probe.style.left = '-99999px';
    probe.style.top = '-99999px';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.zIndex = '-1';

    const surface = document.createElement('div');
    surface.className = 'template-field__surface';
    surface.append(this.buildFieldPlaceholderContent(field));
    probe.append(surface);
    pageElement.append(probe);

    const measured = surface.getBoundingClientRect();
    probe.remove();

    if (measured.width <= 0 || measured.height <= 0) {
      return null;
    }

    const width = clampNormalizedSize(
      (measured.width + MEASURED_FIELD_SAFETY_PX) / pageRect.width,
      MIN_MEASURED_FIELD_WIDTH,
      MAX_MEASURED_FIELD_WIDTH,
    );
    const height = clampNormalizedSize(
      (measured.height + MEASURED_FIELD_SAFETY_PX) / pageRect.height,
      MIN_MEASURED_FIELD_HEIGHT,
      MAX_MEASURED_FIELD_HEIGHT,
    );

    return { x: 0.12, y: 0.36, width, height };
  }

  private normalizeExistingFieldRectsToContentFit(): void {
    if (this.currentStep !== 2 || this.stepTwoMode !== 'configure' || this.fieldRectsNormalizedForSession) {
      return;
    }
    if (this.draft.fields.length === 0) {
      this.fieldRectsNormalizedForSession = true;
      return;
    }

    let allMeasured = true;
    let anyChanged = false;

    const normalized = this.draft.fields.map((field) => {
      const pageElement = this.resolvePageElement(field.page);
      const measured = this.measureFieldRectFromRenderedContent(field, pageElement);
      if (!measured) {
        allMeasured = false;
        return field;
      }

      const nextRect: TemplateFieldRect = {
        ...field.rect,
        width: measured.width,
        height: measured.height,
      };
      const clampedRect = this.clampRectToPage(nextRect);
      if (this.wouldOverlapField(field.id, field.page, clampedRect)) {
        return field;
      }

      if (
        Math.abs(clampedRect.width - field.rect.width) > 0.0001 ||
        Math.abs(clampedRect.height - field.rect.height) > 0.0001 ||
        Math.abs(clampedRect.x - field.rect.x) > 0.0001 ||
        Math.abs(clampedRect.y - field.rect.y) > 0.0001
      ) {
        anyChanged = true;
        return { ...field, rect: clampedRect };
      }

      return field;
    });

    this.fieldRectsNormalizedForSession = allMeasured;
    if (anyChanged) {
      this.draft.fields = normalized;
      this.scheduleOverlaySync();
      this.cdr.markForCheck();
    }
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private clampRectToPage(rect: TemplateFieldRect): TemplateFieldRect {
    const width = this.clamp(rect.width, 0.0001, 1);
    const height = this.clamp(rect.height, 0.0001, 1);
    const x = this.clamp(rect.x, 0, 1 - width);
    const y = this.clamp(rect.y, 0, 1 - height);
    return { x, y, width, height };
  }

  private rectsOverlap(a: TemplateFieldRect, b: TemplateFieldRect): boolean {
    const epsilon = 0.0001;
    return (
      a.x + a.width > b.x + epsilon &&
      b.x + b.width > a.x + epsilon &&
      a.y + a.height > b.y + epsilon &&
      b.y + b.height > a.y + epsilon
    );
  }

  private wouldOverlapField(fieldId: string | null, page: number, candidate: TemplateFieldRect): boolean {
    return this.draft.fields.some((field) => {
      if (field.page !== page) {
        return false;
      }
      if (fieldId && field.id === fieldId) {
        return false;
      }
      return this.rectsOverlap(field.rect, candidate);
    });
  }

  private syncRoleColorRegistry(): void {
    const activeRoleIds = new Set(this.draft.roles.map((role) => role.id));

    for (const [roleId, color] of Array.from(this.roleColorRegistry.entries())) {
      if (activeRoleIds.has(roleId)) {
        continue;
      }

      this.roleColorRegistry.delete(roleId);
      if (!ROLE_COLOR_PALETTE.includes(color)) {
        this.releasedGeneratedRoleColors.push(color);
      }
    }

    for (const role of this.draft.roles) {
      if (this.roleColorRegistry.has(role.id)) {
        continue;
      }

      this.roleColorRegistry.set(role.id, this.nextRoleColor());
    }
  }

  private nextRoleColor(): string {
    const usedColors = new Set(this.roleColorRegistry.values());

    for (const paletteColor of ROLE_COLOR_PALETTE) {
      if (!usedColors.has(paletteColor)) {
        return paletteColor;
      }
    }

    while (this.releasedGeneratedRoleColors.length > 0) {
      const recycledColor = this.releasedGeneratedRoleColors.shift();
      if (recycledColor && !usedColors.has(recycledColor)) {
        return recycledColor;
      }
    }

    let nextColor = this.generateDynamicRoleColor(this.roleColorGeneratedIndex);
    this.roleColorGeneratedIndex += 1;

    while (usedColors.has(nextColor)) {
      nextColor = this.generateDynamicRoleColor(this.roleColorGeneratedIndex);
      this.roleColorGeneratedIndex += 1;
    }

    return nextColor;
  }

  private generateDynamicRoleColor(index: number): string {
    const hue = (ROLE_COLOR_HUE_OFFSET_DEGREES + index * ROLE_COLOR_GOLDEN_ANGLE_DEGREES) % 360;
    return `hsl(${Math.round(hue)}, 72%, 78%)`;
  }

  private scheduleOverlaySync(): void {
    if (this.overlaySyncTimer) {
      clearTimeout(this.overlaySyncTimer);
    }

    this.overlaySyncTimer = setTimeout(() => {
      this.syncOverlays();
    }, 0);
  }

  private syncOverlays(): void {
    const host = this.viewerHost?.nativeElement;
    if (!host || !this.viewerPdfSource || !this.viewerMounted) {
      return;
    }

    const pages = Array.from(host.querySelectorAll<HTMLElement>('.page[data-page-number]'));
    for (const pageElement of pages) {
      pageElement.style.position = 'relative';

      const pageNumber = Number(pageElement.dataset['pageNumber']);
      if (!Number.isInteger(pageNumber) || pageNumber < 1) {
        continue;
      }

      const overlayLayer = this.ensureOverlayLayer(pageElement, pageNumber);
      overlayLayer.classList.toggle(
        'template-overlay-layer--droppable',
        this.stepTwoMode === 'configure' && this.dragFieldType !== null && this.canPlaceFields,
      );
      overlayLayer.classList.toggle(
        'template-overlay-layer--interacting',
        pageNumber === this.interactingOverlayPage,
      );

      if (!this.canPlaceFields) {
        overlayLayer.classList.remove('template-overlay-layer--drop-hover');
      }

      overlayLayer.replaceChildren();

      const pageFields = this.draft.fields.filter(
        (field) => field.page === pageNumber && this.isFieldVisible(field),
      );

      for (const field of pageFields) {
        const fieldNode = this.buildFieldNode(field);
        overlayLayer.append(fieldNode);
      }
    }
  }

  private ensureOverlayLayer(pageElement: HTMLElement, pageNumber: number): HTMLElement {
    const existing = pageElement.querySelector<HTMLElement>(':scope > .template-overlay-layer');
    if (existing) {
      existing.dataset['pageNumber'] = String(pageNumber);
      return existing;
    }

    const layer = document.createElement('div');
    layer.className = 'template-overlay-layer';
    layer.dataset['pageNumber'] = String(pageNumber);
    layer.addEventListener('dragover', this.onOverlayDragOver);
    layer.addEventListener('dragleave', this.onOverlayDragLeave);
    layer.addEventListener('drop', this.onOverlayDrop);
    pageElement.append(layer);
    return layer;
  }

  private isFieldVisible(field: TemplateField): boolean {
    if (this.stepTwoMode === 'configure') {
      return true;
    }

    if (this.previewRecipientFilter === 'all') {
      return true;
    }

    return field.role === this.previewRecipientFilter;
  }

  private buildFieldNode(field: TemplateField): HTMLElement {
    const element = document.createElement('div');
    element.dataset['fieldId'] = field.id;
    element.className = `template-field template-field--${field.type}`;
    element.setAttribute('aria-label', `${FIELD_LABELS[field.type]} field`);

    if (field.id === this.selectedFieldId) {
      element.classList.add('template-field--selected');
    }

    if (field.id === this.interactingFieldId) {
      element.classList.add('template-field--interacting');
    }

    if (this.stepTwoMode !== 'configure') {
      element.classList.add('template-field--readonly');
    }

    const surface = document.createElement('div');
    surface.className = 'template-field__surface';
    if (field.type === 'signature' && this.signatureFramePreviewSettings.enabled) {
      surface.classList.add('template-field__surface--signature-frame');
    }

    const content = this.buildFieldPlaceholderContent(field);
    surface.append(content);
    element.append(surface);

    if (this.stepTwoMode === 'configure') {
      element.append(this.buildFieldDeleteButton(field.id));

      if (field.id === this.selectedFieldId && this.isFieldResizable(field.type)) {
        element.append(this.buildFieldResizeGrip());
      }
    }

    element.style.setProperty('--role-accent', this.roleChipColor(field.role));
    this.applyFieldRect(element, field.rect);

    element.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (this.stepTwoMode !== 'configure') {
        return;
      }

      this.selectField(field.id);
    });

    if (this.stepTwoMode === 'configure') {
      this.attachInteractHandlers(element, field.type);
    }

    return element;
  }

  private buildFieldPlaceholderContent(field: TemplateField): HTMLElement {
    const settings = normalizeFieldSettings(field.settings);
    const content = document.createElement('div');
    content.className = 'template-field__content';

    content.style.fontFamily = settings.font.family;
    content.style.fontSize = `${settings.font.size}px`;
    content.style.lineHeight = `${settings.font.lineHeight}px`;
    content.style.fontWeight = settings.font.bold ? '700' : '500';
    content.style.fontStyle = settings.font.italic ? 'italic' : 'normal';
    content.style.textDecoration = settings.font.underline ? 'underline' : 'none';
    content.style.color = settings.font.color || '#143459';
    content.style.textAlign = settings.font.align;
    content.style.letterSpacing = field.type === 'name' ? '' : `${settings.font.characterSpacing}px`;
    content.style.direction = settings.font.rtl ? 'rtl' : 'ltr';

    const requiredMarker = field.required ? '*' : '';
    const displayLabel = trimOrUndefined(field.label) ?? trimOrUndefined(field.fieldKey) ?? FIELD_LABELS[field.type];

    if (field.type === 'signature' && this.signatureFramePreviewSettings.enabled) {
      return this.buildSignatureFramePlaceholder();
    }

    if (field.type === 'dropdown') {
      const label = document.createElement('span');
      label.className = 'template-field__label';
      label.textContent = `${displayLabel}${requiredMarker}`;
      content.append(label);

      const caret = document.createElement('span');
      caret.className = 'template-field__caret';
      caret.textContent = '▾';
      content.append(caret);
      return content;
    }

    if (field.type === 'checkbox') {
      const row = document.createElement('span');
      row.className = 'template-field__choice';
      const box = document.createElement('span');
      box.className = 'template-field__choice-mark';
      row.append(box);
      content.append(row);
      return content;
    }

    if (field.type === 'radio') {
      const row = document.createElement('span');
      row.className = 'template-field__choice';
      const box = document.createElement('span');
      box.className = 'template-field__choice-mark template-field__choice-mark--radio';
      row.append(box);
      content.append(row);
      return content;
    }

    if (field.type === 'image') {
      const icon = document.createElement('span');
      icon.className = 'template-field__image-icon';
      icon.textContent = 'IMG';
      const label = document.createElement('span');
      label.className = 'template-field__label';
      label.textContent = `${trimOrUndefined(settings.image?.title) ?? displayLabel}${requiredMarker}`;
      content.append(icon, label);
      return content;
    }

    if (field.type === 'date_signed' || field.type === 'editable_date') {
      const dateText = document.createElement('span');
      dateText.className = 'template-field__label';
      dateText.textContent = `${settings.date?.dateFormat ?? 'MM/dd/yyyy'}${requiredMarker}`;
      content.append(dateText);
      return content;
    }

    if (field.type === 'textbox') {
      const label = document.createElement('span');
      label.className = 'template-field__label';
      const fallback =
        trimOrUndefined(settings.textbox?.hintText) ??
        trimOrUndefined(settings.textbox?.defaultText) ??
        displayLabel;
      label.textContent = `${fallback}${requiredMarker}`;
      content.append(label);
      return content;
    }

    const label = document.createElement('span');
    label.className = 'template-field__label';
    label.textContent = `${displayLabel}${requiredMarker}`;
    content.append(label);
    return content;
  }

  private get signatureFramePreviewSettings(): BrandSignatureFrameSettings {
    const selectedBrandId = this.draft.documentSettings.brandId?.trim();
    if (!selectedBrandId) {
      return DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
    }

    return (
      this.brands.find((brand) => brand.brandId === selectedBrandId)?.signatureFrame ??
      DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS
    );
  }

  private buildSignatureFramePlaceholder(): HTMLElement {
    const settings = this.signatureFramePreviewSettings;
    const content = document.createElement('div');
    content.className = 'template-field__content template-field__signature-frame';

    const header = document.createElement('span');
    header.className = 'template-field__signature-frame-header';
    header.textContent = settings.showTimestamp ? 'XlhireEsigns · Jul 21, 2026, 06:09 PM' : 'XlhireEsigns';
    content.append(header);

    const box = document.createElement('div');
    box.className = 'template-field__signature-frame-box';
    const boxBody = document.createElement('div');
    boxBody.className = 'template-field__signature-frame-box-body';

    const signature = document.createElement('span');
    signature.className = 'template-field__signature-script';
    signature.textContent = 'John Doe';
    boxBody.append(signature);
    box.append(boxBody);
    content.append(box);

    const footer = document.createElement('div');
    footer.className = 'template-field__signature-frame-footer';

    if (settings.showRecipientName) {
      const name = document.createElement('span');
      name.className = 'template-field__signature-meta';
      name.textContent = 'John Doe';
      footer.append(name);
    }

    if (settings.showRecipientEmail) {
      const email = document.createElement('span');
      email.className = 'template-field__signature-meta';
      email.textContent = 'johndoe@example.com';
      footer.append(email);
    }

    const documentId = document.createElement('span');
    documentId.className = 'template-field__signature-docid';
    documentId.textContent = '1fc5d60f-46ca-41b9-a5d6-fad53595bf43';
    footer.append(documentId);

    content.append(footer);
    return content;
  }

  private buildFieldDeleteButton(fieldId: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'template-field__delete';
    button.setAttribute('aria-label', 'Remove field');
    button.innerHTML = '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>';

    const swallowPointerDown = (event: Event): void => {
      event.stopPropagation();
    };

    button.addEventListener('pointerdown', swallowPointerDown);
    button.addEventListener('mousedown', swallowPointerDown);
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.removeField(fieldId);
    });

    return button;
  }

  private buildFieldResizeGrip(): HTMLElement {
    const grip = document.createElement('span');
    grip.className = 'template-field__resize-grip';
    grip.setAttribute('aria-hidden', 'true');
    return grip;
  }

  private attachInteractHandlers(element: HTMLElement, fieldType: TemplateFieldType): void {
    interact(element).unset();

    const interactable = interact(element);
    interactable.preventDefault('always');
    interactable.draggable({
      allowFrom: '.template-field__surface',
      ignoreFrom: '.template-field__delete, .template-field__resize-grip',
      listeners: {
        start: (event: any) => {
          this.zone.run(() => {
            this.beginFieldInteraction(event.target as HTMLElement);
          });
        },
        move: (event: any) => {
          this.zone.run(() => {
            this.onDragMove(event as { target: HTMLElement; dx: number; dy: number });
          });
        },
        end: (event: any) => {
          this.zone.run(() => {
            this.endFieldInteraction(event.target as HTMLElement);
          });
        },
      },
    });

    if (this.isFieldResizable(fieldType)) {
      interactable.resizable({
        allowFrom: '.template-field__resize-grip',
        edges: {
          left: false,
          right: '.template-field__resize-grip',
          top: false,
          bottom: '.template-field__resize-grip',
        },
        margin: 0,
        ignoreFrom: '.template-field__delete',
        listeners: {
          start: (event: any) => {
            this.zone.run(() => {
              this.beginFieldInteraction(event.target as HTMLElement);
            });
          },
          move: (event: any) => {
            this.zone.run(() => {
              this.onResizeMove(
                event as {
                  target: HTMLElement;
                  rect: { width: number; height: number };
                  deltaRect: { left: number; top: number };
                },
              );
            });
          },
          end: (event: any) => {
            this.zone.run(() => {
              this.endFieldInteraction(event.target as HTMLElement);
            });
          },
        },
      });
    }

    interactable.styleCursor(false);
  }

  private beginFieldInteraction(target: HTMLElement): void {
    const fieldElement = target.closest<HTMLElement>('.template-field');
    const pageElement = target.closest<HTMLElement>('.page[data-page-number]');
    const overlayLayer = pageElement?.querySelector<HTMLElement>(':scope > .template-overlay-layer') ?? null;
    const fieldId = fieldElement?.dataset['fieldId'] ?? null;
    const pageNumber = Number(pageElement?.dataset['pageNumber']);

    if (!fieldElement || !fieldId || !overlayLayer || !Number.isInteger(pageNumber) || pageNumber < 1) {
      return;
    }

    if (this.interactingFieldId !== fieldId || this.interactingOverlayPage !== pageNumber) {
      this.clearFieldInteractionState();
    }

    this.interactingFieldId = fieldId;
    this.interactingOverlayPage = pageNumber;
    fieldElement.classList.add('template-field--interacting');
    overlayLayer.classList.add('template-overlay-layer--interacting');
  }

  private endFieldInteraction(_target?: HTMLElement | null): void {
    this.clearFieldInteractionState();
  }

  private clearFieldInteractionState(): void {
    const host = this.viewerHost?.nativeElement;
    host?.querySelectorAll<HTMLElement>('.template-field--interacting').forEach((element) => {
      element.classList.remove('template-field--interacting');
    });
    host?.querySelectorAll<HTMLElement>('.template-overlay-layer--interacting').forEach((element) => {
      element.classList.remove('template-overlay-layer--interacting');
    });

    this.interactingFieldId = null;
    this.interactingOverlayPage = null;
  }

  private onDragMove(event: { target: HTMLElement; dx: number; dy: number }): void {
    const fieldId = event.target.dataset['fieldId'];
    const pageElement = event.target.closest<HTMLElement>('.page[data-page-number]');
    if (!fieldId || !pageElement) {
      return;
    }

    const pageRect = pageElement.getBoundingClientRect();
    const currentField = this.draft.fields.find((field) => field.id === fieldId);
    if (!currentField || pageRect.width <= 0 || pageRect.height <= 0) {
      return;
    }

    const nextRect: TemplateFieldRect = this.clampRectToPage({
      ...currentField.rect,
      x: currentField.rect.x + event.dx / pageRect.width,
      y: currentField.rect.y + event.dy / pageRect.height,
    });
    if (this.wouldOverlapField(fieldId, currentField.page, nextRect)) {
      return;
    }

    this.patchField(fieldId, { rect: nextRect }, false);
    this.applyFieldRect(event.target, nextRect);
  }

  private isFieldResizable(type: TemplateFieldType): boolean {
    return !NON_RESIZABLE_FIELD_TYPES.has(type);
  }

  private onResizeMove(event: {
    target: HTMLElement;
    rect: { width: number; height: number };
    deltaRect: { left: number; top: number };
  }): void {
    const fieldId = event.target.dataset['fieldId'];
    const pageElement = event.target.closest<HTMLElement>('.page[data-page-number]');
    if (!fieldId || !pageElement) {
      return;
    }

    const pageRect = pageElement.getBoundingClientRect();
    const currentField = this.draft.fields.find((field) => field.id === fieldId);
    if (!currentField || pageRect.width <= 0 || pageRect.height <= 0) {
      return;
    }
    if (!this.isFieldResizable(currentField.type)) {
      return;
    }

    const minSize = 0.02;
    const width = this.clamp(event.rect.width / pageRect.width, minSize, 1);
    const height = this.clamp(event.rect.height / pageRect.height, minSize, 1);

    const nextRect: TemplateFieldRect = this.clampRectToPage({
      x: currentField.rect.x + event.deltaRect.left / pageRect.width,
      y: currentField.rect.y + event.deltaRect.top / pageRect.height,
      width,
      height,
    });
    if (this.wouldOverlapField(fieldId, currentField.page, nextRect)) {
      return;
    }

    this.patchField(fieldId, { rect: nextRect }, false);
    this.applyFieldRect(event.target, nextRect);
  }

  private updateSelectedField(patch: Partial<TemplateField>, syncOverlay = true): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    this.patchField(selected.id, patch, syncOverlay);
    this.cdr.markForCheck();
  }

  private updateSelectedFieldSettings(mutator: (settings: TemplateFieldSettings) => TemplateFieldSettings): void {
    const selected = this.selectedField;
    if (!selected) {
      return;
    }

    const nextSettings = normalizeFieldSettingsForType(selected.type, mutator(normalizeFieldSettings(selected.settings)));
    this.patchField(selected.id, { settings: nextSettings });
    this.cdr.markForCheck();
  }

  private patchField(fieldId: string, patch: Partial<TemplateField>, syncOverlay = true): void {
    this.draft.fields = this.draft.fields.map((field) => {
      if (field.id !== fieldId) {
        return field;
      }

      const merged: TemplateField = {
        ...field,
        ...patch,
      };

      return {
        ...merged,
        fieldKey: this.normalizedFieldKey(merged),
        settings: normalizeFieldSettingsForType(merged.type, merged.settings),
      };
    });

    if (syncOverlay) {
      this.scheduleOverlaySync();
    }
  }

  private normalizedFieldKey(field: Pick<TemplateField, 'id' | 'type' | 'fieldKey'>): string {
    const trimmed = trimOrUndefined(field.fieldKey);
    if (trimmed) {
      return trimmed;
    }

    const prefix = defaultFieldKeyPrefix(field.type);
    const index = this.draft.fields.filter((entry) => entry.type === field.type).findIndex((entry) => entry.id === field.id);
    if (index >= 0) {
      return `${prefix}${index + 1}`;
    }

    return `${prefix}1`;
  }

  private applyFieldRect(element: HTMLElement, rect: TemplateFieldRect): void {
    element.style.left = `${rect.x * 100}%`;
    element.style.top = `${rect.y * 100}%`;
    element.style.width = `${rect.width * 100}%`;
    element.style.height = `${rect.height * 100}%`;
  }

  private readonly onOverlayDragOver = (event: DragEvent): void => {
    if (!this.canPlaceFields) {
      return;
    }

    const dataTransfer = event.dataTransfer;
    const fieldType = this.resolveDraggedFieldType(dataTransfer);
    if (!fieldType) {
      return;
    }

    event.preventDefault();
    if (dataTransfer) {
      dataTransfer.dropEffect = 'copy';
    }
    (event.currentTarget as HTMLElement | null)?.classList.add('template-overlay-layer--drop-hover');
  };

  private readonly onOverlayDragLeave = (event: DragEvent): void => {
    (event.currentTarget as HTMLElement | null)?.classList.remove('template-overlay-layer--drop-hover');
  };

  private readonly onOverlayDrop = (event: DragEvent): void => {
    const targetLayer = event.currentTarget as HTMLElement | null;
    targetLayer?.classList.remove('template-overlay-layer--drop-hover');

    if (!this.canPlaceFields) {
      return;
    }

    const type = this.resolveDraggedFieldType(event.dataTransfer);
    if (!type || !targetLayer) {
      return;
    }

    event.preventDefault();
    this.dragFieldType = null;

    const pageNumber = Number(targetLayer.dataset['pageNumber'] ?? targetLayer.closest<HTMLElement>('.page[data-page-number]')?.dataset['pageNumber']);
    if (!Number.isInteger(pageNumber) || pageNumber < 1) {
      return;
    }

    const pageElement =
      targetLayer.closest<HTMLElement>('.page[data-page-number]') ??
      this.resolvePageElement(pageNumber) ??
      targetLayer;
    const pageRect = pageElement.getBoundingClientRect();
    if (pageRect.width <= 0 || pageRect.height <= 0) {
      return;
    }

    const activeRole = this.activeRoleId || this.draft.roles[0]?.id || '';
    if (!activeRole) {
      this.setStepTwoError('Add a role before configuring fields.');
      return;
    }

    const rectSeed = this.computeFitRectForNewField(type, activeRole, pageNumber, pageElement);
    if (!rectSeed) {
      this.setStepTwoError('Please wait for page rendering to finish, then drop the field again.');
      this.scheduleOverlaySync();
      this.cdr.markForCheck();
      return;
    }
    const rawX = (event.clientX - pageRect.left) / pageRect.width;
    const rawY = (event.clientY - pageRect.top) / pageRect.height;
    const rect = this.clampRectToPage({
      ...rectSeed,
      x: rawX - rectSeed.width / 2,
      y: rawY - rectSeed.height / 2,
    });
    if (this.wouldOverlapField(null, pageNumber, rect)) {
      this.setStepTwoError("Can't place field on top of another field.");
      this.scheduleOverlaySync();
      this.cdr.markForCheck();
      return;
    }

    const createdField = this.createField(type, activeRole, pageNumber, rect);
    this.draft.fields = [...this.draft.fields, createdField];
    this.selectedFieldId = createdField.id;
    this.stepTwoError = '';
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  };

  private clearDropHoverClasses(): void {
    this.viewerHost?.nativeElement
      .querySelectorAll<HTMLElement>('.template-overlay-layer--drop-hover')
      .forEach((layer) => layer.classList.remove('template-overlay-layer--drop-hover'));
  }

  private resolveDraggedFieldType(dataTransfer: DataTransfer | null): TemplateFieldType | null {
    const value = this.dragFieldType ?? dataTransfer?.getData('text/plain') ?? '';
    return this.normalizeTemplateFieldType(value);
  }

  private normalizeTemplateFieldType(value: string): TemplateFieldType | null {
    if (!value) {
      return null;
    }

    if (!TEMPLATE_FIELD_TYPES.includes(value as TemplateFieldType)) {
      return null;
    }

    return value as TemplateFieldType;
  }

  private createField(
    type: TemplateFieldType,
    roleId: string,
    page: number,
    rect?: TemplateFieldRect,
  ): TemplateField {
    const fieldKey = this.nextFieldKey(type);
    const settings = this.createFieldSettingsWithUserDefaults();
    if (type === 'checkbox') {
      settings.checkbox.optionGroupId = fieldKey;
    }
    if (type === 'radio') {
      settings.radio.groupName = fieldKey;
    }

    return {
      id: crypto.randomUUID(),
      fieldKey,
      type,
      role: roleId,
      page,
      rect: rect ?? this.defaultRectByType(type),
      required: true,
      label: FIELD_LABELS[type],
      placeholder: undefined,
      settings,
    };
  }

  private currentUserDateSettings(): TemplateDateSettings {
    const defaults = this.userDateTimeFormat.dateFieldDefaults();
    return {
      dateFormat: defaults.dateFormat,
      timeFormat: defaults.timeFormat,
      defaultDate: undefined,
    };
  }

  private createFieldSettingsWithUserDefaults(): TemplateFieldSettings {
    return {
      ...createDefaultFieldSettings(),
      date: this.currentUserDateSettings(),
    };
  }

  private nextFieldKey(type: TemplateFieldType): string {
    const prefix = defaultFieldKeyPrefix(type);
    const count = this.draft.fields.filter((field) => field.type === type).length + 1;
    return `${prefix}${count}`;
  }

  private forceViewerRerender(reason: string): void {
    this.viewerRenderNonce += 1;
    this.viewerMounted = false;
    this.showViewerInstance = false;
    this.tracePdfEvent('viewer-remount-start', { reason, viewerRenderNonce: this.viewerRenderNonce });

    setTimeout(() => {
      if (this.currentStep !== 2 || this.viewerPdfSource === null) {
        this.tracePdfEvent('viewer-remount-aborted', {
          reason,
          currentStep: this.currentStep,
          hasSource: this.viewerPdfSource !== null,
        });
        return;
      }

      this.showViewerInstance = true;
      this.viewerMounted = true;
      this.tracePdfEvent('viewer-remount-complete', {
        reason,
        viewerRenderNonce: this.viewerRenderNonce,
      });
      this.cdr.markForCheck();
    }, 0);
  }

  private finalizePdfLoaded(reason: string, payload?: unknown): void {
    if (!this.pdfLoading) {
      this.tracePdfEvent('pdfLoadFinalizeSkipped', { reason, cause: 'not-loading', payload });
      return;
    }

    this.clearPdfStuckSnapshotTimer();
    this.clearPdfBootstrapWatchdogTimer();
    this.pdfLoading = false;
    this.pdfLoaded = true;
    this.pdfBootstrapError = '';
    this.pdfLoadError = '';
    this.pdfLoadErrorDetail = '';
    this.stepTwoError = '';
    this.tracePdfEvent('pdfLoadFinalized', { reason, payload });
    this.scheduleOverlaySync();
    this.cdr.markForCheck();
  }

  private extractPdfErrorDetail(error: unknown): string {
    if (!error) {
      return '';
    }

    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message.trim();
    }

    if (typeof error === 'string' && error.trim().length > 0) {
      return error.trim();
    }

    if (typeof error === 'object') {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim().length > 0) {
        return message.trim();
      }
    }

    return '';
  }

  private shouldIgnoreFieldDeleteShortcut(target: EventTarget | null): boolean {
    const activeElement =
      target instanceof HTMLElement
        ? target
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

    if (!activeElement) {
      return false;
    }

    if (activeElement.isContentEditable) {
      return true;
    }

    return (
      activeElement.matches(
        'input, textarea, select, button, a[href], [role="button"], [role="textbox"], [role="combobox"], [contenteditable], [tabindex]:not([tabindex="-1"])',
      ) ||
      activeElement.closest(
        'input, textarea, select, button, a[href], [role="button"], [role="textbox"], [role="combobox"], [contenteditable], [tabindex]:not([tabindex="-1"])',
      ) !== null
    );
  }

  private notePdfViewerEvent(eventName: string, payload?: unknown): void {
    this.pdfEventCountThisAttempt += 1;
    this.tracePdfEvent(eventName, {
      eventCountThisAttempt: this.pdfEventCountThisAttempt,
      payload,
    });
  }

  private tracePdfEvent(eventName: string, payload?: unknown): void {
    if (!this.enablePdfEventTrace) {
      return;
    }

    const sourceKind = this.pdfObjectUrl ? 'blob' : 'none';
    const state: PdfTraceState = {
      currentStep: this.currentStep,
      viewerMounted: this.viewerMounted,
      pdfLoading: this.pdfLoading,
      pdfLoaded: this.pdfLoaded,
      pageCount: this.pageCount,
      activePage: this.activePage,
      hasSource: this.viewerPdfSource !== null,
      eventCountThisAttempt: this.pdfEventCountThisAttempt,
      retryCount: this.pdfBootstrapRetryCount,
      viewerRenderNonce: this.viewerRenderNonce,
    };

    const payloadText = this.stringifyPdfTracePayload(payload);
    const entry: PdfTraceEntry = {
      id: (this.pdfTraceEntryIdCounter += 1),
      timestampIso: new Date().toISOString(),
      event: eventName,
      attemptId: this.pdfLoadAttemptId,
      sourceKind,
      state,
      payloadText,
    };

    this.pdfTraceEntries = [...this.pdfTraceEntries, entry].slice(-PDF_TRACE_BUFFER_SIZE);

  }

  private beginPdfLoadAttempt(origin: string): void {
    this.pdfLoadAttemptId += 1;
    this.pdfEventCountThisAttempt = 0;
    this.pdfBootstrapRetryCount = 0;
    this.pdfBootstrapError = '';
    this.viewerMounted = this.viewerPdfSource !== null;
    this.showViewerInstance = true;
    this.clearPdfStuckSnapshotTimer();
    this.clearPdfBootstrapWatchdogTimer();
    this.tracePdfEvent('pdfLoadAttemptStarted', { origin });
    this.schedulePdfStuckSnapshot();
    this.schedulePdfBootstrapWatchdog();
  }

  private schedulePdfStuckSnapshot(): void {
    this.clearPdfStuckSnapshotTimer();
    if (!this.pdfLoading) {
      return;
    }

    const attemptId = this.pdfLoadAttemptId;
    this.pdfStuckSnapshotTimer = setTimeout(() => {
      if (!this.pdfLoading || this.pdfLoaded || attemptId !== this.pdfLoadAttemptId) {
        return;
      }

      this.tracePdfEvent('pdfLoadStuckSnapshot', this.buildPdfDomSnapshot());
      this.cdr.markForCheck();
    }, PDF_STUCK_SNAPSHOT_MS);
  }

  private clearPdfStuckSnapshotTimer(): void {
    if (!this.pdfStuckSnapshotTimer) {
      return;
    }

    clearTimeout(this.pdfStuckSnapshotTimer);
    this.pdfStuckSnapshotTimer = null;
  }

  private schedulePdfBootstrapWatchdog(): void {
    this.clearPdfBootstrapWatchdogTimer();
    const attemptId = this.pdfLoadAttemptId;
    this.pdfBootstrapWatchdogTimer = setTimeout(() => {
      if (!this.pdfLoading || this.pdfLoaded || attemptId !== this.pdfLoadAttemptId) {
        return;
      }

      const snapshot = this.buildPdfDomSnapshot();
      const noViewerEvents = this.pdfEventCountThisAttempt === 0;
      const noRenderedPages = this.readSnapshotCount(snapshot, 'pageNodeCount') === 0;
      if (!noViewerEvents || !noRenderedPages) {
        this.tracePdfEvent('bootstrap-watchdog-skip', {
          noViewerEvents,
          noRenderedPages,
          snapshot,
          eventCountThisAttempt: this.pdfEventCountThisAttempt,
        });
        return;
      }

      if (this.pdfBootstrapRetryCount < PDF_BOOTSTRAP_MAX_RETRIES) {
        this.performBootstrapRetry('watchdog-no-events', snapshot);
        return;
      }

      this.pdfLoading = false;
      this.pdfLoaded = false;
      this.pdfBootstrapError = PDF_BOOTSTRAP_FAILURE_MESSAGE;
      this.enrichBootstrapErrorWithRuntimeProbe(attemptId);
      this.tracePdfEvent('bootstrap-retry-result', {
        result: 'failed-after-retries',
        retryCount: this.pdfBootstrapRetryCount,
        snapshot,
      });
      this.cdr.markForCheck();
    }, PDF_BOOTSTRAP_WATCHDOG_MS);
  }

  private clearPdfBootstrapWatchdogTimer(): void {
    if (!this.pdfBootstrapWatchdogTimer) {
      return;
    }

    clearTimeout(this.pdfBootstrapWatchdogTimer);
    this.pdfBootstrapWatchdogTimer = null;
  }

  private performBootstrapRetry(reason: string, snapshot?: Record<string, unknown>): void {
    const selectedFile = this.draft.selectedFile;
    if (!selectedFile) {
      this.pdfBootstrapError = 'Preview failed to initialize. Please reselect the PDF in Step 1.';
      this.tracePdfEvent('bootstrap-retry-result', { result: 'no-selected-file', reason });
      return;
    }

    this.pdfBootstrapRetryCount += 1;
    this.tracePdfEvent('bootstrap-retry-start', {
      reason,
      retryCount: this.pdfBootstrapRetryCount,
      eventCountThisAttempt: this.pdfEventCountThisAttempt,
      snapshot,
    });

    this.clearPdfSources();
    this.pdfEventCountThisAttempt = 0;
    this.pdfLoading = true;
    this.pdfLoaded = false;
    this.pdfLoadError = '';
    this.pdfLoadErrorDetail = '';
    this.pdfBootstrapError = '';
    this.dragFieldType = null;

    try {
      this.pdfObjectUrl = URL.createObjectURL(selectedFile);
      this.viewerMounted = true;
      this.tracePdfEvent('source-created', {
        fileName: selectedFile.name,
        sizeBytes: selectedFile.size,
        type: selectedFile.type || 'unknown',
        via: 'bootstrap-retry',
      });
      this.forceViewerRerender('bootstrap-retry');
      this.schedulePdfStuckSnapshot();
      this.schedulePdfBootstrapWatchdog();
      this.tracePdfEvent('bootstrap-retry-result', {
        result: 'source-recreated',
        retryCount: this.pdfBootstrapRetryCount,
      });
    } catch (error) {
      this.pdfLoading = false;
      this.pdfBootstrapError = PDF_BOOTSTRAP_FAILURE_MESSAGE;
      this.enrichBootstrapErrorWithRuntimeProbe(this.pdfLoadAttemptId);
      this.tracePdfEvent('bootstrap-retry-result', {
        result: 'source-recreate-failed',
        retryCount: this.pdfBootstrapRetryCount,
        error,
      });
    }

    this.cdr.markForCheck();
  }

  private enrichBootstrapErrorWithRuntimeProbe(attemptId: number): void {
    if (typeof window === 'undefined' || typeof fetch !== 'function') {
      return;
    }

    const probes = this.buildRuntimeAssetProbeTargets();
    if (probes.length === 0) {
      return;
    }

    void Promise.all(probes.map((probe) => this.probeRuntimeAsset(probe.label, probe.url))).then((results) => {
      if (
        attemptId !== this.pdfLoadAttemptId ||
        this.pdfLoaded ||
        this.pdfBootstrapError.length === 0 ||
        !this.pdfBootstrapError.startsWith(PDF_BOOTSTRAP_FAILURE_MESSAGE)
      ) {
        return;
      }

      const failures = results.filter((result) => !result.ok);
      if (failures.length === 0) {
        this.tracePdfEvent('bootstrap-runtime-asset-probe', { result: 'all-ok', results });
        return;
      }

      const detail = failures
        .map((failure) => {
          const statusPart = failure.status !== null ? String(failure.status) : 'network-error';
          const notePart = failure.note ? ` (${failure.note})` : '';
          return `${failure.label}: ${statusPart}${notePart}`;
        })
        .join(' | ');

      this.pdfBootstrapError = `${PDF_BOOTSTRAP_FAILURE_MESSAGE} Failed checks: ${detail}.`;
      this.tracePdfEvent('bootstrap-runtime-asset-probe', { result: 'failures', results });
      this.cdr.markForCheck();
    });
  }

  private buildRuntimeAssetProbeTargets(): Array<{ label: string; url: string }> {
    const workerSrc = this.resolveRuntimeAssetUrl(pdfDefaultOptions.workerSrc());
    if (!workerSrc) {
      return [];
    }

    const targets: Array<{ label: string; url: string }> = [{ label: 'worker', url: workerSrc }];
    if (workerSrc.includes('pdf.worker-')) {
      targets.push({
        label: 'viewer',
        url: workerSrc.replace('pdf.worker-', 'viewer-'),
      });
    }

    return targets.filter((target, index, array) => {
      return (
        target.url.length > 0 &&
        array.findIndex((candidate) => candidate.label === target.label && candidate.url === target.url) === index
      );
    });
  }

  private resolveRuntimeAssetUrl(candidate: string): string {
    if (!candidate) {
      return '';
    }

    try {
      return new URL(candidate, document.baseURI).toString();
    } catch {
      return candidate;
    }
  }

  private async probeRuntimeAsset(label: string, url: string): Promise<RuntimeAssetProbeResult> {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        cache: 'no-store',
      });

      return {
        label,
        url,
        ok: response.ok,
        status: response.status,
      };
    } catch (headError) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: { Range: 'bytes=0-0' },
          cache: 'no-store',
        });

        return {
          label,
          url,
          ok: response.ok || response.status === 206,
          status: response.status,
          note: 'head-fallback-get',
        };
      } catch (fallbackError) {
        return {
          label,
          url,
          ok: false,
          status: null,
          note: this.extractPdfErrorDetail(fallbackError || headError) || 'request-failed',
        };
      }
    }
  }

  private buildPdfDomSnapshot(): Record<string, unknown> {
    const host = this.viewerHost?.nativeElement;
    if (!host) {
      return {
        hasViewerHost: false,
        pageNodeCount: 0,
        overlayLayerCount: 0,
        loadingBannerCount: 0,
        hostWidth: 0,
        hostHeight: 0,
        computedDisplay: 'none',
        computedVisibility: 'hidden',
      };
    }

    const hostRect = host.getBoundingClientRect();
    const computedDisplay = host.style.display || 'unknown';
    const computedVisibility = host.style.visibility || 'unknown';

    return {
      hasViewerHost: true,
      pageNodeCount: host.querySelectorAll('.page[data-page-number]').length,
      overlayLayerCount: host.querySelectorAll('.template-overlay-layer').length,
      loadingBannerCount: host.querySelectorAll('.viewer-loading').length,
      hostWidth: Math.round(hostRect.width),
      hostHeight: Math.round(hostRect.height),
      computedDisplay,
      computedVisibility,
      isConnected: host.isConnected,
    };
  }

  private readSnapshotCount(snapshot: Record<string, unknown>, key: string): number {
    const value = snapshot[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    return 0;
  }

  private stringifyPdfTracePayload(payload: unknown): string {
    if (payload === undefined) {
      return '';
    }

    if (payload instanceof Error) {
      return payload.message;
    }

    if (typeof payload === 'string') {
      return payload;
    }

    try {
      return JSON.stringify(payload);
    } catch {
      return '[unserializable payload]';
    }
  }
}
